import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime, Settings } from "luxon";
import {
	createContext,
	type Dispatch,
	type FC,
	type ReactNode,
	type SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { FullPageLoadingFallback } from "../components/LoadingFallback/FullPageLoadingFallback";
import { departmentRosterQueryKey } from "../hooks/nursesQueryKeys";
import { usePatchRosterNurse, useReadRosterNurse } from "../hooks/rosterCache";
import { scheduleWagesKeys } from "../hooks/scheduleCache";
import { invalidateSchedule, type ScheduleInvalidation } from "../hooks/scheduleCacheWrites";
import { useDepartmentData } from "../hooks/useDepartmentData";
import { useGetDepartmentDisplayName } from "../hooks/useDepartmentDisplayName";
import { useDepartmentFlags } from "../hooks/useDepartmentFlags";
import { useDepartmentWageData } from "../hooks/useDepartmentWageData";
import { type ShowErrorSnackbar, useErrorSnackbar } from "../hooks/useErrorSnackbar";
import { NURSE_SCHEDULE_QUERY_KEY, useInvalidateNurseQueries } from "../hooks/useInvalidateNurseQueries";
import { TEAM_QUERY_KEY, TEAM_STATS_QUERY_KEY, useInvalidateTeamQueries } from "../hooks/useInvalidateTeamQueries";
import { useNurses } from "../hooks/useNurses";
import { useFeatureFlag } from "../hooks/useOpenFeatureFlag";
import { useScheduleCacheEnabled } from "../hooks/useScheduleWeekRules";
import { backendService } from "../services/backend.service";
import { localStorageService } from "../services/LocalStorageService";
import { HOSPITAL_DEMO, NEUTRAL } from "../theme/tokens";
import { trackErrorEvent } from "../utils/analytics";
import type { Suggestion } from "../utils/autoStaffing";
import { buildNurseShift } from "../utils/buildNurseShift";
import {
	deepCloneObject,
	getOtWeekStart,
	getPtoStaffingDays,
	getShiftDisplayDate,
	getShiftTypeFromStartTime,
	getWeekStart,
	setDefaultTimezone,
} from "../utils/dateUtils";
import {
	applyNurseDetails,
	camelCaseWeekShifts,
	copyDayData,
	countShiftRows,
	findScheduleSlot,
	nurseShiftsInTimeFrame,
	type ScheduleDayShift,
	type ScheduleSlot,
	scheduleDayOfShift,
	scheduledShiftsByIdOf,
	withNurseDayTrainingStatus,
} from "../utils/departmentWeeks";
import { formatPersonFullName } from "../utils/formatters";
import {
	AbsentReason,
	type ActualNurse,
	type BaseNurseShift,
	type Day,
	DEFAULT_WEEK_START_DAY,
	type DepartmentLocation,
	DepartmentNames,
	type DepartmentWageDataResponse,
	type EmploymentType,
	type EmrType,
	type HospitalData,
	type Nurse,
	NurseFullRole,
	NurseFullRoleShort,
	type NurseInShift,
	NurseRole,
	type NurseTrainingStatus,
	Preferences,
	type ScheduledProcedure,
	type Shift,
	type ShiftTimes,
	type ShiftType,
	type SpecificPreference,
	SuggestionAction,
	type TransformedShift,
	type WeekData,
} from "../utils/models";
import axiosWrapper from "../utils/networkWrapper";
import { roleAverageWagesOf } from "../utils/roleAverageWages";
import { withMoveSource, withMoveTarget, withShiftDetailsEdited } from "../utils/shiftEditLocal";
import { elapsedMs, measureAsync } from "../utils/timing";
import {
	getDepartmentNurseTypes,
	getShiftTypeByShiftTimes,
	inverseRolesMap,
	isHeadNurse,
	processApiResponse,
	resolveActiveEmrType,
} from "../utils/utilFunctions";
import { transformedShiftsOf } from "../utils/viewerShifts";
import { accessToken, type Department, useAuth } from "./AuthProvider";
import { useAnalytics } from "./analytics/hooks";
import { useChosenDepartment } from "./ChosenDepartment";
import { DEFAULT_CHOSEN_DEPARTMENT } from "./ChosenDepartment/consts";
import { FEATURE_FLAGS } from "./FeatureFlagsProvider";
import { useSnackbar } from "./SnackbarContext";
import { calculateShiftTimes } from "./utils/shift-times";
import { useWeekIndex } from "./WeekIndexProvider";

export interface ShiftIndex {
	week: number;
	shift: number;
	day: number;
}

export interface NurseChanges {
	additions: ShiftIndex[];
	removals: ShiftIndex[];
}

/** An edit of the old list: the data in, an edited copy out. */
export type ScheduleEdit = (data: HospitalData) => HospitalData;
/** Makes the edit `edit` makes to the old list's `date` to the drafted copy of that day too. */
export type DraftDayWriter = (date: DateTime, edit: ScheduleEdit) => void;

export interface HospitalDataContextType {
	hospitalData: HospitalData;
	setHospitalData: Dispatch<SetStateAction<HospitalData>>;
	swapNurseId: number;
	swapNurseWithId: number;
	swapDayNo: number;
	swapDayNoWith: number;
	swapShiftNo: number;
	chosenDepartment: Department;
	locationsMap: Map<string, DepartmentLocation>;
	timezone: string;
	dateIndexMap: Map<string, { weekIndex: number; dayIndex: number }>;
	setChosenDepartment: Dispatch<SetStateAction<Department>>;
	isSnf: boolean;
	isAlf: boolean;
	isHospital: boolean;
	isAnyDemo: boolean;
	/** Live "real demo" dept (inhouse2__) that runs the actual product features on real data,
	 * unlike isAnyDemo which serves legacy fake/stub data. Mirrors backend is_real_demo_dept. */
	isRealDemo: boolean;
	isHospitalDemo: boolean;
	/** Hospital visuals for a real hospital demo dept; unlike isHospitalDemo, no mock bypasses. */
	isHospitalView: boolean;
	/** The Holland ICU/Telemetry demo specifically — gates its per-area Shift-Insights breakdown. */
	isIcuTeleDemo: boolean;
	isSnfDemo: boolean;
	isAlfDemo: boolean;
	isLowry: boolean;
	isColonial: boolean;
	refreshDepartmentData: (department_id: string, update_week_index: boolean, date?: DateTime, dontUpdateScheduleUntilDate?: boolean) => Promise<void>;
	refreshDepartmentWageData: () => Promise<void>;
	refreshNursesData: (department_id: string, date?: DateTime) => Promise<void>;
	isAutoScheduler: boolean;
	isInitialLoading: boolean;
	wageData: DepartmentWageDataResponse | null;
	setSwapNurseId: (id: number) => void;
	setSwapNurseWithId: (id: number) => void;
	/** Where `shiftType` on schedule day `date` sits in `hospitalData.weeks`, or `undefined` when that day isn't loaded. */
	getScheduleSlot: (date: DateTime, shiftType: string) => ScheduleSlot | undefined;
	/** The schedule day a shift belongs to (the department's day start decides) and the shift type it's listed under. */
	scheduleDayOf: (shift: Pick<NurseInShift, "startTime" | "shiftType">) => ScheduleDayShift;
	/** The schedule days of these loaded scheduled shifts, or `null` when any of them isn't loaded. */
	getShiftScheduleDates: (shiftIds: string[]) => DateTime[] | null;
	/**
	 * Run after a save reaches the server: reloads the schedule as it does today and marks the saved dates' cached weeks
	 * stale, or every cached week when the save's dates aren't known.
	 */
	afterScheduleWrite: (target: ScheduleInvalidation) => Promise<void>;
	/**
	 * After a publish of shift changes: the old schedule reloads only when the server asks (`shouldRefresh`), as before,
	 * since otherwise the caller's local edit stands in for the reload. The saved dates' cached weeks are always marked
	 * stale, since that local edit changes the old schedule only.
	 */
	afterPublish: (shouldRefresh: boolean, dates: DateTime[]) => Promise<void>;
	/** Marks cached weeks stale without reloading the old schedule, for a save that reloads it its own way. */
	refreshScheduleCache: (target: ScheduleInvalidation) => Promise<void>;
	/** Edits `date` in the old schedule, and in the drafted day while a draft is open on the schedule cache. */
	writeScheduleDay: (date: DateTime, edit: ScheduleEdit) => void;
	/** Draft mode's writer for the drafted day while a draft is open on the schedule cache; null when none is. */
	setDraftDayWriter: (writer: DraftDayWriter | null) => void;
	acceptNurseChanges: (date: DateTime, shiftType: string, nurseId: number, isActual: boolean) => void;
	addMessageToNurse: (date: DateTime, shiftType: string, nurseId: number, message: string) => void;
	addNurseToShift: (nurseInShift: NurseInShift, date: DateTime, shiftType: string, addedFromActual?: boolean, isDraftMode?: boolean) => void;
	declineNurseChanges: (date: DateTime, shiftType: string, nurseId: number, isAutoScheduler: boolean) => void;
	removeNurseFromSpecificDate: (
		date: DateTime,
		shiftType: string,
		nurseId: number,
		removedFromActual?: boolean,
		id?: string,
		isDraftMode?: boolean,
		message?: string,
		forceRemove?: boolean,
	) => void;
	updateNurse: (updatedNurse: Nurse) => void;
	moveNurseToSpecificDate: (fromDate: DateTime, fromShiftType: string, toDate: DateTime, toShiftType: string, nurseId: number, newNurseId?: string) => void;
	restoreNurseToSpecificDate: (date: DateTime, shiftType: string, nurseId: number, restoredFromActual?: boolean, isDraftMode?: boolean) => void;
	swapNurseFromSpecificDate: () => void;
	setSwapDayNo: (dayNo: number) => void;
	setSwapDayNoWith: (dayNo: number) => void;
	setSwapShiftNo: (shiftNo: number) => void;
	setSwapShiftNoWith: (shiftNo: number) => void;
	getWeekIndexByDateTime: (date: DateTime) => number;
	getDayIndexByDateTime: (date: DateTime) => number;
	getWeekIndexByDate: (date: string | DateTime) => number;
	getDayIndexByDate: (date: string | DateTime) => number;
	getNurseTransformedShifts: (nurseId: number) => TransformedShift[];
	getNurseFormattedName: (nurseId: Nurse["id"]) => string;
	getNurseById: (id: number) => Nurse | undefined;
	scheduledShiftsById: Map<string, NurseInShift>;
	activeBedsideNurse: Nurse | undefined;
	removeNurseCompletelyFromShift: (date: DateTime, shiftType: string, nurseId: number, showActual?: boolean) => void;
	removeNursesShiftsCompletely: (shiftsToRemove: string[]) => void;
	addNewDBNurseShifts: (shiftsToAdd: NurseInShift[]) => void;
	reorderNurses: () => void;
	getColorByDifficultyName: (difficulty: string | undefined) => string;
	getNurseNameFromId: (id: number) => string;
	implementedChanges: Record<string, boolean>;
	setImplementedChanges: Dispatch<SetStateAction<Record<string, boolean>>>;
	handleSuggestion: (suggestion: Suggestion, date: DateTime, shiftType: string, shiftTimes?: ShiftTimes) => void;
	weekI: number;
	setWeekI: Dispatch<SetStateAction<number>>;
	getNurseShifts: (nurseId: number, timeFrameStart?: DateTime, timeFrameEnd?: DateTime) => number;
	prepareDataForAddNurse: (
		hospitalData: HospitalData,
		date: DateTime,
		shiftType: string,
		nurseId: number,
		location?: string,
		addedFromActual?: boolean,
		requestId?: string,
		shiftTimes?: ShiftTimes,
		nurseInShiftId?: string,
		shiftBonus?: number,
		isDraftMode?: boolean,
	) => NurseInShift | undefined;
	getNurseWeekendShifts: (nurseId: number, dateFrameStart?: DateTime, dateFrameEnd?: DateTime) => number;
	getNurseRDOs: (nurseId: number, dateFrameStart?: DateTime, dateFrameEnd?: DateTime) => number;
	getNurseRole: (nurse: Nurse) => React.ReactNode;
	getNurseRoleEnum: (nurseId: number) => NurseRole | null;
	updateNurseLocation: (id: string, date: DateTime, shiftType: string, newLocation: string, actual: boolean) => void;
	updateShiftBonus: (id: string, date: DateTime, shiftType: string, newBonus: number, actual?: boolean, isDraftMode?: boolean) => void;
	updateNurseNotes: (id: string, date: DateTime, shiftType: string, newNotes: string, actual: boolean) => void;
	updateNurseTimes: (
		nurseInShiftId: string,
		date: DateTime,
		shiftType: string,
		newStartTime: DateTime,
		newEndTime: DateTime,
		actual: boolean,
		isDraftMode?: boolean,
	) => void;
	updateNurseLocationAndTimes: (params: {
		nurseInShiftId: string;
		date: DateTime;
		shiftType: string;
		newStartTime?: DateTime;
		newEndTime?: DateTime;
		actual: boolean;
		newLocation?: string;
		newJobId?: string;
		newJobName?: string;
		isDraftMode?: boolean;
	}) => void;
	showErrorSnackbar: ShowErrorSnackbar;
	getShiftTimes: (
		day: Day,
		shiftNo: number,
		nurseId: number,
		location?: string,
	) => {
		start: DateTime;
		end: DateTime;
	};
	calculateShiftTimes: (
		day: Pick<Day, "date">,
		shiftNo: number,
		fullNurse: NurseFullRole | undefined,
		location?: string,
		section?: string,
	) => { startTime: DateTime; endTime: DateTime };
	updateNurseChargeStatus: (nurseInShiftId: string, date: DateTime, shiftType: string, isCharge: boolean, isDraftMode?: boolean) => void;
	/** Sets a nurse's training status on every shift of theirs on `date`, scheduled and actual, across shift types. */
	updateNurseDayTrainingStatus: (date: DateTime, nurseId: number, trainingStatus: NurseTrainingStatus, isDraftMode: boolean) => void;

	transformedShifts: TransformedShift[];
	getShiftDate: (weekIndex: number, shiftNo: number, dayNo: number) => DateTime;
	getWeekRange: (referenceDate: DateTime) => { startDate: string; endDate: string };
	departmentNurseTypes: NurseFullRole[];
	userIdNurseMap: { [userId: string]: Nurse };
	latestDateInWeeksData: DateTime | undefined;
	roleAverageWage: Partial<Record<NurseFullRole, string>>;
	departmentEmrType: EmrType | undefined;
	getDepartmentDisplayName: (departmentId: string) => string;
	getNurseShiftsInTimeFrame: (nurseId: number, timeFrameStart?: DateTime, timeFrameEnd?: DateTime, isActual?: boolean) => NurseInShift[];
	calculateDefaultTimesAndLocationForStaff: (staffId: number) => Array<{
		startTime: string;
		endTime: string;
		location: string;
	}>;
	getNurseToWeeklyShifts: (_: Nurse["id"]) => Shift[];
	isMedicalDepartment: boolean;
}

export const HospitalDataContext = createContext<HospitalDataContextType>({
	hospitalData: {} as HospitalData,
	setHospitalData: () => {},
	swapNurseId: -1,
	swapNurseWithId: -1,
	swapDayNo: -1,
	swapDayNoWith: -1,
	swapShiftNo: -1,
	chosenDepartment: DEFAULT_CHOSEN_DEPARTMENT,
	locationsMap: new Map<string, DepartmentLocation>(),
	timezone: "",
	isSnf: false,
	isAlf: false,
	activeBedsideNurse: undefined,
	isHospital: false,
	isAnyDemo: false,
	isRealDemo: false,
	isHospitalDemo: false,
	isHospitalView: false,
	isIcuTeleDemo: false,
	isSnfDemo: false,
	isAlfDemo: false,
	isLowry: false,
	isColonial: false,
	isAutoScheduler: false,
	isInitialLoading: true,
	wageData: null,
	refreshDepartmentData: async (department_id: string, update_week_index: boolean, date?: DateTime, dontUpdateScheduleUntilDate?: boolean) => {},
	refreshDepartmentWageData: async () => {},
	refreshNursesData: async (department_id: string, date?: DateTime) => {},
	setChosenDepartment: () => {},
	setSwapNurseId: () => {},
	setSwapNurseWithId: () => {},
	acceptNurseChanges: () => {},
	addMessageToNurse: () => {},
	getScheduleSlot: () => undefined,
	scheduleDayOf: (shift) => ({ date: shift.startTime, shiftType: shift.shiftType }),
	getShiftScheduleDates: () => null,
	afterScheduleWrite: async () => {},
	afterPublish: async () => {},
	refreshScheduleCache: async () => {},
	writeScheduleDay: () => {},
	setDraftDayWriter: () => {},
	addNurseToShift: () => {},
	declineNurseChanges: () => {},
	removeNurseFromSpecificDate: () => {},
	updateNurse: () => {},
	moveNurseToSpecificDate: () => {},
	restoreNurseToSpecificDate: () => {},
	swapNurseFromSpecificDate: () => {},
	setSwapDayNo: () => {},
	setSwapDayNoWith: () => {},
	setSwapShiftNo: () => {},
	setSwapShiftNoWith: () => {},
	getWeekIndexByDateTime: () => -1,
	getDayIndexByDateTime: () => -1,
	getWeekIndexByDate: () => -1,
	getDayIndexByDate: () => -1,
	removeNurseCompletelyFromShift: () => {},
	removeNursesShiftsCompletely: () => {},
	addNewDBNurseShifts: () => {},
	getNurseShiftsInTimeFrame: () => [],
	getColorByDifficultyName: () => "",
	reorderNurses: () => {},
	getNurseNameFromId: () => "",
	implementedChanges: {},
	setImplementedChanges: () => {},
	handleSuggestion: () => {},
	weekI: 0,
	setWeekI: () => {},
	getNurseShifts: () => 0,
	getNurseWeekendShifts: () => 0,
	getNurseRDOs: () => 0,
	getNurseRole: () => <></>,
	getNurseRoleEnum: () => null,
	updateNurseLocation: () => {},
	updateNurseNotes: () => {},
	updateNurseTimes: () => {},
	updateNurseLocationAndTimes: (params) => {},
	updateShiftBonus: () => {},
	showErrorSnackbar: () => {},
	getShiftTimes: () => ({ start: DateTime.now(), end: DateTime.now() }),
	dateIndexMap: new Map(),
	calculateShiftTimes: () => ({ startTime: DateTime.now(), endTime: DateTime.now() }),
	updateNurseChargeStatus: () => {},
	updateNurseDayTrainingStatus: () => {},
	getNurseTransformedShifts: (nurseId: number) => [],
	getNurseFormattedName: (nurseId: number) => "",
	getNurseById: (id: number) => undefined,
	scheduledShiftsById: new Map<string, NurseInShift>(),
	prepareDataForAddNurse: () => undefined,
	transformedShifts: [],
	getShiftDate: () => DateTime.now(),
	getWeekRange: () => ({ startDate: "", endDate: "" }),
	departmentNurseTypes: [],
	userIdNurseMap: {},
	latestDateInWeeksData: undefined,
	roleAverageWage: {},
	departmentEmrType: undefined,
	getDepartmentDisplayName: () => "",
	calculateDefaultTimesAndLocationForStaff: () => [{ startTime: "06:00", endTime: "18:00", location: "" }],
	getNurseToWeeklyShifts: (_) => [],
	isMedicalDepartment: false,
});

function processProcs(procs: ScheduledProcedure[], twoShiftDayNight = false): Record<string, Record<string, Array<ScheduledProcedure>>> {
	let proceduresView = localStorage.getItem("proceduresView");
	if (!proceduresView) {
		proceduresView = "Procedure Time";
	}

	const response: Record<string, Record<string, Array<ScheduledProcedure>>> = {};
	for (const proc of procs) {
		const dateToSort = proceduresView === "By Admission" ? proc.departmentArrivalTimeMin : proc.activityDate;
		const endDate = proceduresView === "By Admission" ? proc.departmentArrivalTimeMax : dateToSort.plus({ minutes: 1 });

		// 2-shift Day/Night hospitals (the inhouse2__ real demo) bucket by Day (06:00-18:00) /
		// Night. The 3-shift model (Assuta, legacy InHouse__Hospital_Demo) buckets
		// Morning/Evening/Night at 07:00/15:00/23:00 — so those keep the default path.
		if (twoShiftDayNight) {
			addProcToDayNightShift(response, proc, dateToSort);
			continue;
		}

		let shiftStartTime = getShiftStartTime(dateToSort);
		while (shiftStartTime < endDate) {
			const hoursDiff = addProcToShift(response, proc, shiftStartTime, dateToSort);
			shiftStartTime = shiftStartTime.plus({ hour: hoursDiff });
		}
	}

	const sortedData: Record<string, Record<string, Array<ScheduledProcedure>>> = {};
	for (const outerKey in response) {
		sortedData[outerKey] = {};
		for (const innerKey in response[outerKey]) {
			sortedData[outerKey][innerKey] = response[outerKey][innerKey].sort((a, b) => {
				if (proceduresView === "By Admission") return a.departmentArrivalTimeMin.valueOf() - b.departmentArrivalTimeMin.valueOf();
				return a.activityDate.valueOf() - b.activityDate.valueOf();
			});
		}
	}

	return sortedData;
}

function addProcToShift(
	response: Record<string, Record<string, Array<ScheduledProcedure>>>,
	proc: ScheduledProcedure,
	shiftStartTime: DateTime,
	dateToSort: DateTime,
): number {
	const shiftType = shiftStartTime.hour === 7 ? "Morning" : shiftStartTime.hour === 15 ? "Evening" : "Night";
	const dateString = (shiftStartTime.toISO() as string).split("T")[0];
	if (!response[dateString]) {
		response[dateString] = {};
	}
	const dateDict = response[dateString];
	dateDict[shiftType] = dateDict[shiftType] || [];
	dateDict[shiftType].push(proc);

	if (shiftType === "Morning" && dateToSort.hour === 14 && dateToSort.minute >= 30) {
		dateDict.Evening = dateDict.Evening || [];
		dateDict.Evening.push(proc);
		return 16;
	}
	return 8;
}

// Bucket a procedure into a 2-shift Day/Night hospital by its start hour. Day = 06:00-18:00;
// Night = 18:00-06:00. A Night procedure before 06:00 belongs to the previous calendar day's
// Night shift, matching how the staffing grid buckets with day_start=06:00.
function addProcToDayNightShift(response: Record<string, Record<string, Array<ScheduledProcedure>>>, proc: ScheduledProcedure, dateToSort: DateTime): void {
	const shiftType = getShiftTypeFromStartTime(dateToSort);
	const shiftDate = dateToSort.hour < 6 ? dateToSort.minus({ days: 1 }) : dateToSort;
	const dateString = (shiftDate.toISO() as string).split("T")[0];
	response[dateString] ??= {};
	response[dateString][shiftType] ??= [];
	response[dateString][shiftType].push(proc);
}

function getShiftStartTime(d: DateTime): DateTime {
	const hour = d.hour;
	if (hour >= 23) {
		return d.set({ hour: 23 }).set({ minute: 0 }).set({ second: 0 }).set({ millisecond: 0 });
	}
	if (hour >= 15) {
		return d.set({ hour: 15 }).set({ minute: 0 }).set({ second: 0 }).set({ millisecond: 0 });
	}
	if (hour >= 7) {
		return d.set({ hour: 7 }).set({ minute: 0 }).set({ second: 0 }).set({ millisecond: 0 });
	}

	const newD = d.minus({ days: 1 }).set({ hour: 23 }).set({ minute: 0 }).set({ second: 0 }).set({ millisecond: 0 });
	return newD;
}

function findCurrentWeekIndex(weeks: WeekData[], department_id: string, date?: DateTime, timezone?: string): number {
	// For Cheyanna the schedule starts for the previous schedule week, so we want to start from the second week
	if (department_id === DepartmentNames.AutoScheduler) return 1;

	const now = timezone ? DateTime.now().setZone(timezone) : DateTime.now();
	const currentSunday = date ? date.startOf("week") : now.startOf("week");
	const currentSundayStr = currentSunday.toFormat("yyyy-MM-dd");

	// Loop through weeks to find matching Sunday
	const index = weeks.findIndex((week) => {
		const weekSunday = week.weeklyShifts[0].days[0].date.toFormat("yyyy-MM-dd");
		return weekSunday === currentSundayStr;
	});

	// Return 0 if no match found, otherwise return the found index
	return index === -1 ? 0 : index;
}

/** `shifts` with `update` applied to the first shift of `nurseId`; the same list when there's none. */
const withFirstOfNurse = <Shift extends { nurseId: number }>(shifts: Shift[], nurseId: number, update: (shift: Shift) => Shift): Shift[] => {
	const index = shifts.findIndex((shift) => shift.nurseId === nurseId);
	return index === -1 ? shifts : shifts.map((shift, at) => (at === index ? update(shift) : shift));
};

const SCHEDULE_LOAD_EVENT = "schedule_load";
const ROSTER_LOAD_EVENT = "roster_load";
const INITIAL_DATA_LOAD_EVENT = "initial_data_load";
// Tells today's whole-window load apart from the per-week schedule cache that replaces it.
const SCHEDULE_DATA_SOURCE = "department_data";
type ScheduleLoadTrigger = "initial" | "refresh";
// `errorMessage` is set when any part of the load fails, including parts that report their failure instead of throwing.
type InitialLoadTiming = { departmentId: string; startedAt: number; finished: boolean; errorMessage: string | null };

const loadTrigger = (load: InitialLoadTiming | null): ScheduleLoadTrigger => (load === null ? "refresh" : "initial");

const failInitialLoad = (load: InitialLoadTiming | null, errorMessage: string) => {
	if (load) load.errorMessage = errorMessage;
};

export const HospitalDataProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const { t } = useTranslation();
	const { isLoggedIn, logout, userInfo } = useAuth();

	const [hospitalData, setHospitalData] = useState<HospitalData>({
		patients: [],
		nurses: [],
		weeks: [],
		procedures: {},
		feedbacks: [],
	});

	const { chosenDepartment, setChosenDepartment } = useChosenDepartment();
	const locationsMap = useMemo(() => new Map(chosenDepartment.locations.map((loc) => [loc.id, loc])), [chosenDepartment]);

	const {
		timezone,
		isAutoScheduler,
		isLowry,
		isColonial,
		isSnf,
		isHospital,
		isAlf,
		isHospitalDemo,
		isSnfDemo,
		isAlfDemo,
		isRealDemo,
		isHospitalView,
		isIcuTeleDemo,
		isAnyDemo,
		isMedicalDepartment,
	} = useDepartmentFlags();
	const shouldReverseNameFormat = useFeatureFlag(FEATURE_FLAGS.NameFormatLastFirst);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);

	const [swapNurseId, setSwapNurseId] = useState(-1);
	const [swapNurseWithId, setSwapNurseWithId] = useState(-1);
	const [swapDayNo, setSwapDayNo] = useState(-1);
	const [swapShiftNo, setSwapShiftNo] = useState(-1);
	const [swapShiftNoWith, setSwapShiftNoWith] = useState(-1);
	const [swapDayNoWith, setSwapDayNoWith] = useState(-1);
	const [weekI, setWeekI] = useState<number>(0);
	const { weekIndex, setWeekIndex } = useWeekIndex();
	const { showSnackbar } = useSnackbar();
	const [implementedChanges, setImplementedChanges] = useState<Record<string, boolean>>({});
	const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);

	const wantDate = chosenDepartment.start_date?.toFormat("yyyy-MM-dd") || DateTime.now().setZone(timezone).toFormat("yyyy-MM-dd");

	const { refetch: refetchDepartmentData } = useDepartmentData(chosenDepartment.department_id, wantDate, timezone);
	const { data: wageData = null, refetch: refetchDepartmentWageData } = useDepartmentWageData(chosenDepartment.department_id, wantDate);
	const { refetch: refetchNurses } = useNurses(chosenDepartment.department_id);
	// With it on, the app never loads department_data, the wages or the staff list: screens read the schedule cache,
	// the per-week wages, and the roster query (`useRosterLookup`), which loads when a screen first reads it.
	// `hospitalData.nurses` stays empty.
	const isCacheEnabled = useScheduleCacheEnabled();
	const queryClient = useQueryClient();
	const readRosterNurse = useReadRosterNurse();
	// The profile grid reads its own per-nurse copy of the weeks, so every weeks write below also marks it stale.
	const invalidateNurseQueries = useInvalidateNurseQueries();
	// The Team page reads its own roster and stat cards, so roster refreshes and department refreshes mark them stale too.
	const invalidateTeamQueries = useInvalidateTeamQueries();
	const { setUser, track, trackError } = useAnalytics();
	// Set while a department's first load runs, so its schedule and roster loads are told apart from later refreshes.
	const initialLoad = useRef<InitialLoadTiming | null>(null);
	const hasFinishedFirstLoad = useRef(false);

	const getWeekRange = useCallback(
		(referenceDate: DateTime): { startDate: string; endDate: string } => {
			if (!referenceDate.isValid) return { startDate: "", endDate: "" };
			const weekStart = getOtWeekStart(referenceDate, chosenDepartment.week_start_day);
			return {
				startDate: weekStart.toISODate() ?? "",
				endDate: weekStart.plus({ days: 7 }).toISODate() ?? "",
			};
		},
		[chosenDepartment.week_start_day],
	);

	const departmentNurseTypesCalc = useMemo(
		() => getDepartmentNurseTypes(chosenDepartment.roles, hospitalData.nurses),
		[hospitalData.nurses, chosenDepartment.roles],
	);

	// We are doing this to prevent re-creation if content is the same.
	const departmentNurseTypes = useMemo(() => {
		return departmentNurseTypesCalc;
	}, [departmentNurseTypesCalc]);

	function getShiftDate(weekIndex: number, shiftNo: number, dayNo: number): DateTime {
		return hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo].date;
	}

	const calculateShiftTimesWithTZ = useCallback(
		(
			day: Pick<Day, "date">,
			shiftNo: number,
			nurseRole: NurseFullRole | undefined,
			location?: string,
			section?: string,
		): { startTime: DateTime; endTime: DateTime } => {
			return calculateShiftTimes({
				day,
				shiftNo,
				nurseRole,
				chosenDepartment,
				location,
				section,
			});
		},
		[chosenDepartment],
	);

	// Move all useCallback hooks to the top
	const getShiftTimes = useCallback(
		(day: Day, shiftNo: number, nurseId: number, location?: string): { start: DateTime; end: DateTime } => {
			// Use the centralized utility function for consistent shift times across the application
			const nurseRole = hospitalData.nurses.find((n) => n.id === nurseId)?.role || NurseFullRole.RN;
			const { startTime, endTime } = calculateShiftTimesWithTZ(day, shiftNo, nurseRole, location);

			return { start: startTime, end: endTime };
		},
		[hospitalData, calculateShiftTimesWithTZ],
	);

	const nursesMap = useMemo(() => {
		if (hospitalData.nurses?.length) return new Map(hospitalData.nurses.map((nurse) => [nurse.id, nurse]));
		return new Map();
	}, [hospitalData.nurses]);

	const getNurseById = useCallback((id: number): Nurse | undefined => nursesMap.get(id), [nursesMap]);

	const scheduledShiftsById = useMemo(() => scheduledShiftsByIdOf(hospitalData.weeks ?? []), [hospitalData.weeks]);

	const getNurseShiftsInTimeFrame = useCallback(
		(nurseId: number, timeFrameStart?: DateTime, timeFrameEnd?: DateTime, isActual?: boolean) => {
			if (!getNurseById(nurseId) || !hospitalData.weeks) return [];
			return nurseShiftsInTimeFrame(hospitalData.weeks.slice(1), nurseId, timeFrameStart, timeFrameEnd, isActual === true);
		},
		[hospitalData.weeks, getNurseById],
	);

	const getNurseShifts = useCallback(
		(nurseId: number, timeFrameStart?: DateTime, timeFrameEnd?: DateTime) => {
			return getNurseShiftsInTimeFrame(nurseId, timeFrameStart, timeFrameEnd).length;
		},
		[getNurseShiftsInTimeFrame],
	);

	const calculateDefaultTimesAndLocationForStaff = useCallback(
		(staffId: number): Array<{ startTime: string; endTime: string; location: string }> => {
			const DEFAULT_RESULT = [{ startTime: "06:00", endTime: "18:00", location: "" }];

			const findMostCommonTimesAndLocation = (shifts: NurseInShift[]): Array<{ startTime: string; endTime: string; location: string }> | null => {
				if (shifts.length === 0) return null;

				// Count occurrences of each combined location+time key
				const combinedCounts = new Map<string, number>();

				for (const shift of shifts) {
					if (!shift.startTime || !shift.endTime) continue;
					const location = shift.location ?? "";
					const key = `${location}||${shift.startTime.toFormat("HH:mm")}-${shift.endTime.toFormat("HH:mm")}`;
					combinedCounts.set(key, (combinedCounts.get(key) || 0) + 1);
				}

				if (combinedCounts.size === 0) return null;

				// Sort by count descending and take top 2
				const sorted = [...combinedCounts.entries()].sort((a, b) => b[1] - a[1]);
				const top = sorted.slice(0, 2);

				return top.map(([key]) => {
					const [location, times] = key.split("||");
					const [startTime, endTime] = times.split("-");
					return { startTime, endTime, location };
				});
			};

			const getAllShiftsFromWeeks = (): NurseInShift[] => {
				const allShifts: NurseInShift[] = [];
				hospitalData.weeks.forEach((week: WeekData) => {
					week.weeklyShifts.forEach((shift: Shift) => {
						shift.days.forEach((day: Day) => {
							allShifts.push(...day.nurses.filter((n) => !n.isPendingToDelete));
						});
					});
				});
				return allShifts;
			};

			const nurseId = staffId;
			const nurse = getNurseById(nurseId);

			// Step 1: Look at staff's own shifts
			const nurseShifts = getNurseShiftsInTimeFrame(nurseId);
			const resultFromNurseShifts = findMostCommonTimesAndLocation(nurseShifts);
			if (resultFromNurseShifts) {
				return resultFromNurseShifts;
			}

			// Step 2: If no shifts found for the nurse, look at shifts by role
			if (nurse) {
				const allShifts = getAllShiftsFromWeeks();
				const roleShifts = allShifts.filter((shift) => shift.role === nurse.role);
				const resultFromRoleShifts = findMostCommonTimesAndLocation(roleShifts);
				if (resultFromRoleShifts) {
					return resultFromRoleShifts;
				}
			}

			// Step 3: If no role shifts found, look at all shifts
			const allShifts = getAllShiftsFromWeeks();
			const resultFromAllShifts = findMostCommonTimesAndLocation(allShifts);
			if (resultFromAllShifts) {
				return resultFromAllShifts;
			}

			// Step 4: Default to 6a-6p and empty location
			return DEFAULT_RESULT;
		},
		[hospitalData.weeks, getNurseById, getNurseShiftsInTimeFrame],
	);

	const getNurseWeekendShifts = useCallback(
		(nurseId: number, dateFrameStart?: DateTime, dateFrameEnd?: DateTime) => {
			const nurse = hospitalData.nurses?.find((n) => n.id === nurseId);
			if (!nurse || !hospitalData.weeks) return 0;

			let weekendShiftCount = 0;
			hospitalData.weeks.slice(1).forEach((week) => {
				week.weeklyShifts.forEach((shift: Shift) => {
					shift.days.forEach((day: Day) => {
						const dayOfWeek = day.date.weekday % 7;
						const nurseInShift: NurseInShift | undefined = day.nurses.find((n) => n.nurseId === nurseId);
						if (
							nurseInShift !== undefined &&
							!nurseInShift.isPendingToDelete &&
							((!dateFrameStart && !dateFrameEnd) ||
								(dateFrameStart &&
									dateFrameEnd &&
									nurseInShift.startTime >= dateFrameStart &&
									nurseInShift.startTime <= dateFrameEnd &&
									nurseInShift.endTime >= dateFrameStart &&
									nurseInShift.endTime <= dateFrameEnd))
						) {
							if (dayOfWeek === 0 || dayOfWeek === 6) {
								weekendShiftCount++;
							} else if (dayOfWeek === 5 && shift.type === "Night") {
								weekendShiftCount++;
							}
						}
					});
				});
			});

			return weekendShiftCount;
		},
		[hospitalData.nurses, hospitalData.weeks, hospitalData],
	);

	const getNurseRDOs = useCallback(
		(nurseId: number, dateFrameStart?: DateTime, dateFrameEnd?: DateTime) => {
			const nurse = hospitalData.nurses?.find((n) => n.id === nurseId);
			if (!nurse) return 0;

			const rdoSet = new Set();
			nurse.employeeSpecificPreferences.forEach((pref: SpecificPreference) => {
				if (pref.type === Preferences.Avoid && pref.data === AbsentReason.RDO) {
					const startDate = pref.start_date;
					const endDate = pref.end_date;
					if (
						dateFrameStart &&
						dateFrameEnd &&
						(!(startDate >= dateFrameStart && startDate <= dateFrameEnd) || !(endDate >= dateFrameStart && endDate <= dateFrameEnd))
					) {
						return;
					}
					// The old rule hardcoded a 07:00 boundary, which is wrong anywhere day_start is not 07:00.
					for (const day of getPtoStaffingDays(startDate, endDate, chosenDepartment.dayStart)) {
						rdoSet.add(day.toFormat("yyyy-MM-dd"));
					}
				}
			});

			return rdoSet.size;
		},
		[hospitalData],
	);

	const getNurseRole = useCallback((nurse: Nurse) => {
		const roleDisplay = inverseRolesMap[nurse.role];

		return (
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<Typography variant="body2">{roleDisplay}</Typography>
			</Box>
		);
	}, []);

	const getNurseRoleEnum = useCallback(
		(nurseId: number): NurseRole | null => {
			const nurse = getNurseById(nurseId);
			return nurse ? NurseRole[NurseFullRoleShort[nurse.role] as keyof typeof NurseRole] : null;
		},
		[getNurseById],
	);

	const activeBedsideNurse = useMemo<Nurse | undefined>(() => getNurseById(userInfo?.nurses_info?.[0]?.nurse?.id), [userInfo, getNurseById]);

	const getNurseFormattedName = useCallback(
		(nurseId: number): string => {
			const nurse: Nurse | undefined = nursesMap.get(nurseId);
			if (!nurse) {
				trackErrorEvent("invalid_nurse_id", `getNurseFormattedName: Nurse not found for id ${nurseId}`, { nurse_id: nurseId });
				return "";
			}
			return formatPersonFullName(nurse, shouldReverseNameFormat);
		},
		[nursesMap, shouldReverseNameFormat],
	);

	const refreshDepartmentWageData = useCallback(async () => {
		if (chosenDepartment.hide_wage) return;
		if (isCacheEnabled) {
			await queryClient.invalidateQueries({ queryKey: scheduleWagesKeys.department(chosenDepartment.department_id) });
			return;
		}
		await refetchDepartmentWageData();
	}, [chosenDepartment.hide_wage, chosenDepartment.department_id, isCacheEnabled, queryClient, refetchDepartmentWageData]);

	// The one part of department_data the cache doesn't carry.
	const loadUnitsAndFloors = useCallback(
		async (department_id: string): Promise<void> => {
			try {
				const { data } = await backendService.getDepartmentUnitsAndFloors(department_id);
				setHospitalData((prevData) => ({ ...prevData, units: data.units, floors: data.floors }));
			} catch (err) {
				console.error("Failed getting the department's units and floors:", err);
				showSnackbar(t("error.department.data"), "error");
			}
		},
		[showSnackbar, t],
	);

	const refreshDepartmentData = async (department_id: string, update_week_index = false, date?: DateTime, dontUpdateScheduleUntilDate?: boolean) => {
		if (isCacheEnabled) {
			// The display-until date follows user_info (below), so nothing here sets it.
			await Promise.all([invalidateSchedule(queryClient, department_id, { allWeeks: true }), loadUnitsAndFloors(department_id)]);
			void invalidateNurseQueries(NURSE_SCHEDULE_QUERY_KEY);
			void invalidateTeamQueries(TEAM_STATS_QUERY_KEY);
			return;
		}
		// Held from the start: by the time this resolves, a department switch may have started another initial load.
		const load = initialLoad.current;
		const loadEventProps = { trigger: loadTrigger(load), department_id: chosenDepartment.department_id, data_source: SCHEDULE_DATA_SOURCE };
		track(`${SCHEDULE_LOAD_EVENT}_start`, loadEventProps);
		try {
			const [{ value: result, ms: networkMs }] = await Promise.all([
				measureAsync(refetchDepartmentData),
				!chosenDepartment.hide_wage ? refetchDepartmentWageData() : Promise.resolve(null),
			]);

			if (result.isError) {
				trackError(`${SCHEDULE_LOAD_EVENT}_error`, { ...loadEventProps, network_ms: networkMs, error_message: String(result.error) });
				failInitialLoad(load, String(result.error));
			}
			if (!result.data) return;

			const convertStartedAt = performance.now();
			const updatedWeeks = camelCaseWeekShifts(result.data.weeks);
			// A failed refetch still hands back the previous load's data; that isn't a successful load.
			if (!result.isError) {
				track(`${SCHEDULE_LOAD_EVENT}_success`, {
					...loadEventProps,
					network_ms: networkMs,
					convert_ms: elapsedMs(convertStartedAt),
					weeks_loaded: updatedWeeks.length,
					shift_rows: countShiftRows(updatedWeeks),
				});
			}

			if (update_week_index) {
				setWeekIndex(findCurrentWeekIndex(updatedWeeks, department_id, date, timezone));
			}

			if (!dontUpdateScheduleUntilDate && result.data.staff_schedule_display_until_date !== undefined) {
				setChosenDepartment((prevDept) => ({
					...prevDept,
					staffScheduleDisplayUntilDate: result.data.staff_schedule_display_until_date || undefined,
				}));
			}

			setHospitalData((prevData) => {
				updateNurses(prevData.nurses, updatedWeeks, shouldReverseNameFormat);

				return {
					...prevData,
					patients: result.data.patients,
					weeks: updatedWeeks,
					units: result.data.units,
					floors: result.data.floors,
				};
			});
			void invalidateNurseQueries(NURSE_SCHEDULE_QUERY_KEY);
			void invalidateTeamQueries(TEAM_STATS_QUERY_KEY);
		} catch (err) {
			console.error("Failed getting department data:", err);
			trackError(`${SCHEDULE_LOAD_EVENT}_error`, { ...loadEventProps, error_message: String(err) });
			failInitialLoad(load, String(err));
			showSnackbar(t("error.department.data"), "error");
			await logout();
		}
	};

	const departmentId = chosenDepartment.department_id;

	const refreshScheduleCache = useCallback(
		(target: ScheduleInvalidation): Promise<void> => invalidateSchedule(queryClient, departmentId, target),
		[queryClient, departmentId],
	);

	const draftDayWriterRef = useRef<DraftDayWriter | null>(null);
	const setDraftDayWriter = useCallback((writer: DraftDayWriter | null) => {
		draftDayWriterRef.current = writer;
	}, []);
	const writeScheduleDay = useCallback((date: DateTime, edit: ScheduleEdit) => {
		setHospitalData(edit);
		draftDayWriterRef.current?.(date, edit);
	}, []);

	// The provider's in-place edits change the old schedule only, so each also marks its day's cached week stale. A draft
	// edit never reaches the server, so it leaves the cache alone: while a draft is open on the cache, a reload would
	// drop its edits.
	const refreshCachedDay = useCallback(
		(date: DateTime) => {
			if (!draftDayWriterRef.current) void refreshScheduleCache({ dates: [date] });
		},
		[refreshScheduleCache],
	);

	const afterScheduleWrite = async (target: ScheduleInvalidation): Promise<void> => {
		if (isCacheEnabled) {
			// A write doesn't move units or floors: only the written weeks and the per-nurse and team copies change.
			await refreshScheduleCache(target);
			void invalidateNurseQueries(NURSE_SCHEDULE_QUERY_KEY);
			void invalidateTeamQueries(TEAM_STATS_QUERY_KEY);
			return;
		}
		await Promise.all([refreshDepartmentData(departmentId, false), refreshScheduleCache(target)]);
	};

	const afterPublish = (shouldRefresh: boolean, dates: DateTime[]): Promise<void> =>
		shouldRefresh ? afterScheduleWrite({ dates }) : refreshScheduleCache({ dates });

	const getScheduleSlot = useCallback((date: DateTime, shiftType: string) => findScheduleSlot(hospitalData.weeks, date, shiftType), [hospitalData.weeks]);

	const { dayStart, scheduled_shift_delta_minutes } = chosenDepartment;
	const scheduleDayOf = useCallback(
		(shift: Pick<NurseInShift, "startTime" | "shiftType">): ScheduleDayShift => scheduleDayOfShift(shift, { dayStart, scheduled_shift_delta_minutes }),
		[dayStart, scheduled_shift_delta_minutes],
	);

	const getShiftScheduleDates = useCallback(
		(shiftIds: string[]): DateTime[] | null => {
			const shifts = shiftIds.map((shiftId) => scheduledShiftsById.get(shiftId));
			return shifts.every((shift) => shift !== undefined) ? shifts.map((shift) => scheduleDayOf(shift).date) : null;
		},
		[scheduledShiftsById, scheduleDayOf],
	);

	const addNewDBNurseShifts = useCallback(
		(shiftsToAdd: NurseInShift[]) => {
			const updateWeeks = (newShift: NurseInShift, weeks: WeekData[]): WeekData[] => {
				return weeks.map((week) => ({
					...week,
					weeklyShifts: week.weeklyShifts.map((shift) => {
						if (shift.type !== newShift.shiftType) {
							return shift;
						}
						return {
							...shift,
							days: shift.days.map((day) => {
								if (
									!day.date
										.startOf("day")
										.equals(getShiftDisplayDate(newShift.startTime, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).startOf("day"))
								) {
									return day;
								}
								return {
									...day,
									nurses: [...day.nurses, newShift],
								};
							}),
						};
					}),
				}));
			};

			setHospitalData((prevData: HospitalData) => {
				const newData = { ...prevData };

				const nurseMap = new Map(newData.nurses.map((nurse) => [nurse.id, nurse]));
				const newShifts = shiftsToAdd.map((nurseShift) => {
					const fullNurse = nurseMap.get(nurseShift.nurseId);
					if (fullNurse === undefined) return nurseShift;

					return {
						...nurseShift,
						role: fullNurse.role,
						name: formatPersonFullName(fullNurse, shouldReverseNameFormat),
						type: fullNurse.employmentType as EmploymentType,
						isPending: false,
						isPendingToDelete: false,
						isPublished: true,
					};
				});

				newShifts.forEach((newShift: NurseInShift) => {
					newData.weeks = [...updateWeeks(newShift, newData.weeks)];
				});

				return newData;
			});
			void invalidateNurseQueries(NURSE_SCHEDULE_QUERY_KEY);
			void refreshScheduleCache({ dates: shiftsToAdd.map((shift) => scheduleDayOf(shift).date) });
		},
		[
			chosenDepartment.dayStart,
			chosenDepartment.scheduled_shift_delta_minutes,
			shouldReverseNameFormat,
			invalidateNurseQueries,
			refreshScheduleCache,
			scheduleDayOf,
		],
	);

	const removeNursesShiftsCompletely = (shiftsToRemove: string[]) => {
		const shiftsToRemoveSet = new Set(shiftsToRemove);
		const removedDates = getShiftScheduleDates(shiftsToRemove);
		void refreshScheduleCache(removedDates === null ? { allWeeks: true } : { dates: removedDates });

		const updateWeeks = (weeks: WeekData[]) =>
			weeks.map((week) => ({
				...week,
				weeklyShifts: week.weeklyShifts.map((shift) => ({
					...shift,
					days: shift.days.map((day) => ({
						...day,
						nurses: day.nurses.filter((n) => !shiftsToRemoveSet.has(n.id)),
					})),
				})),
			}));

		setHospitalData((prevData: HospitalData) => ({
			...prevData,
			weeks: updateWeeks(prevData.weeks),
		}));
		void invalidateNurseQueries(NURSE_SCHEDULE_QUERY_KEY);
	};

	const removeNurseCompletelyFromShift = (date: DateTime, shiftType: string, nurseId: number, showActual?: boolean) => {
		refreshCachedDay(date);
		writeScheduleDay(date, (prevData: HospitalData) => {
			const slot = findScheduleSlot(prevData.weeks, date, shiftType);
			if (!slot) return prevData;
			const { day, newData } = copyDayData(prevData, slot.weekIndex, slot.shiftIndex, slot.dayIndex);
			if (showActual) {
				day.nursesActual = day.nursesActual?.filter((nurse: ActualNurse) => nurse.nurseId !== nurseId);
			} else {
				day.nurses = day.nurses.filter((nurse: NurseInShift) => nurse.nurseId !== nurseId);
				let nurseActual = day.nursesActual;
				// in case of removing nurse completelyfrom predicted and actual exists then remove from actual also
				if (nurseActual && nurseActual.length > 0 && nurseActual.some((nurse: ActualNurse) => nurse.nurseId === nurseId)) {
					nurseActual = nurseActual.filter((nurse: ActualNurse) => nurse.nurseId !== nurseId);
					day.nursesActual = nurseActual;
				}
			}
			return newData;
		});
	};

	const refreshNursesData = async (department_id?: string, date?: DateTime) => {
		void invalidateTeamQueries(TEAM_QUERY_KEY);
		if (isCacheEnabled) {
			// Screens read the roster query, so this refetches it for the ones showing it.
			await queryClient.invalidateQueries({ queryKey: departmentRosterQueryKey(chosenDepartment.department_id, isCacheEnabled) });
			return;
		}
		const load = initialLoad.current;
		const loadEventProps = { trigger: loadTrigger(load), department_id: chosenDepartment.department_id };
		track(`${ROSTER_LOAD_EVENT}_start`, loadEventProps);
		try {
			const { value: result, ms: networkMs } = await measureAsync(refetchNurses);
			if (result.isError) {
				trackError(`${ROSTER_LOAD_EVENT}_error`, { ...loadEventProps, network_ms: networkMs, error_message: String(result.error) });
				failInitialLoad(load, String(result.error));
			} else {
				// DEFAULT: a successful load with no body means the department has no nurses yet.
				track(`${ROSTER_LOAD_EVENT}_success`, { ...loadEventProps, network_ms: networkMs, nurses_loaded: result.data?.length ?? 0 });
			}
			if (result.data && result.data.length > 0) {
				setHospitalData((prevData) => {
					updateNurses(result.data, prevData.weeks, shouldReverseNameFormat);

					return {
						...prevData,
						nurses: result.data,
						weeks: [...prevData.weeks],
					};
				});
			}
		} catch (err) {
			trackError(`${ROSTER_LOAD_EVENT}_error`, { ...loadEventProps, error_message: String(err) });
			failInitialLoad(load, String(err));
			showSnackbar(t("error.nurses.data"), "error");
		}
	};

	const getProceduresData = async (department_id: string) => {
		axiosWrapper
			.get(`/procedures/by_date?department_id=${department_id}&want_date=${DateTime.now().toFormat("yyyy-MM-dd")}`)
			.then((response) => {
				const procedures = processApiResponse(response.data);
				const proceduresSorted = processProcs(procedures, isHospitalView);
				setHospitalData((prevData) => ({
					...prevData,
					procedures: proceduresSorted,
				}));
			})
			.catch((err) => {
				showSnackbar(t("error.procedures.data"), "error");
			});
	};

	const reorderNurses = () => {
		setHospitalData((prevData) => {
			const procs: ScheduledProcedure[] = Object.values(prevData.procedures)
				.flatMap((innerRecord) => Object.values(innerRecord))
				.flat()
				.reduce<ScheduledProcedure[]>((uniqueProcs, proc) => {
					if (!uniqueProcs.some((p) => p.hashPatientId === proc.hashPatientId)) {
						uniqueProcs.push(proc);
					}
					return uniqueProcs;
				}, []);

			const proceduresSorted = processProcs(procs, isHospitalView);

			return {
				...prevData,
				procedures: proceduresSorted,
			};
		});
	};

	const getfeedbacks = async (department_id: string) => {
		axiosWrapper
			.get(`/departments/weekly_feedbacks?department_id=${department_id}`)
			.then((response) => {
				const feedbacks = processApiResponse(response.data);
				setHospitalData((prevData) => ({
					...prevData,
					feedbacks: feedbacks,
				}));
			})
			.catch((err) => {
				showSnackbar(t("error.feedbacks.data"), "error");
			});
	};

	// Flags refetch without remounting the provider, so a name-order flip in LaunchDarkly would otherwise leave the
	// already-loaded shift names (printout, grids, tooltips) in the old order until the next schedule refresh.
	const appliedNameOrderRef = useRef(shouldReverseNameFormat);
	useEffect(() => {
		if (appliedNameOrderRef.current === shouldReverseNameFormat) return;
		appliedNameOrderRef.current = shouldReverseNameFormat;
		setHospitalData((prevData) => ({ ...prevData, weeks: withShiftNamesInOrder(prevData.nurses, prevData.weeks, shouldReverseNameFormat) }));
	}, [shouldReverseNameFormat]);

	useEffect(() => {
		if (isLoggedIn && userInfo?.departments?.length) {
			setHospitalData((prevData) => ({ ...prevData, departments: userInfo.departments }));
		}
	}, [isLoggedIn, userInfo]);

	useEffect(() => {
		if (chosenDepartment.department_id && userInfo?.username) {
			setUser({
				userId: userInfo.id,
				departmentId: chosenDepartment.department_id,
				role: userInfo.role,
				firstName: userInfo.first_name,
				// Roles are per-department, so they come off the membership for the department in view,
				// not off the top-level user — the same person can hold different roles per building.
				adminRoles: userInfo.departments?.find((dept) => dept.department_id === chosenDepartment.department_id)?.adminRoles ?? [],
			});
		}
	}, [chosenDepartment, userInfo, setUser]);

	// Stored only when the chosen department CHANGES, never on a `userInfo` refresh. A department switch stores the
	// new id and then refreshes `userInfo` while the old department is still chosen; writing then put the old id back,
	// and on a URL with no department in it (`/careers`) the stored id is the only thing that says which one to choose.
	useEffect(() => {
		if (chosenDepartment.department_id) {
			localStorageService.setChosenDepartmentID(chosenDepartment.department_id);
		}
	}, [chosenDepartment.department_id]);

	const dateIndexMap = useMemo(() => {
		const newMap = new Map();
		hospitalData.weeks.forEach((week, weekIdx) => {
			week.weeklyShifts?.[0]?.days.forEach((day, dayIdx) => {
				newMap.set(day.date.toFormat("yyyy-MM-dd"), {
					weekIndex: weekIdx,
					dayIndex: dayIdx,
				});
			});
		});
		return newMap;
	}, [hospitalData]);

	const swapNurseFromSpecificDate = () => {
		if (swapNurseId !== -1 && swapNurseWithId !== -1 && swapDayNo !== -1 && swapDayNoWith !== -1 && swapShiftNo !== -1 && swapShiftNoWith !== -1) {
			setHospitalData((prevData) => {
				const currWeek = { ...prevData.weeks[weekIndex] };
				// Get the nurse arrays from both days
				const sourceNurses = currWeek.weeklyShifts[swapShiftNo].days[swapDayNo].nurses;
				const targetNurses = currWeek.weeklyShifts[swapShiftNoWith].days[swapDayNoWith].nurses;

				// Find the indexes of the nurses being swapped
				const sourceNurseIndex = sourceNurses.findIndex((nurse: any) => nurse.nurseId === swapNurseId);
				const targetNurseIndex = targetNurses.findIndex((nurse: any) => nurse.nurseId === swapNurseWithId);

				if (sourceNurseIndex !== -1 && targetNurseIndex !== -1) {
					// Store the original shift and day for both nurses before swapping
					sourceNurses[sourceNurseIndex].originalShift = swapShiftNo;
					sourceNurses[sourceNurseIndex].originalDay = swapDayNo;
					targetNurses[targetNurseIndex].originalShift = swapShiftNoWith;
					targetNurses[targetNurseIndex].originalDay = swapDayNoWith;
					sourceNurses[sourceNurseIndex].isPending = true;
					sourceNurses[sourceNurseIndex].isPendingToSwap = true;
					targetNurses[targetNurseIndex].isPending = true;
					targetNurses[targetNurseIndex].isPendingToSwap = true;
					sourceNurses[sourceNurseIndex].swapWith = swapNurseWithId;
					targetNurses[targetNurseIndex].swapWith = swapNurseId;

					// Swap the nurses
					[sourceNurses[sourceNurseIndex], targetNurses[targetNurseIndex]] = [targetNurses[targetNurseIndex], sourceNurses[sourceNurseIndex]];

					// Mark both nurses as pending for swap
					sourceNurses[sourceNurseIndex].isPendingToSwap = true;
					targetNurses[targetNurseIndex].isPendingToSwap = true;

					sourceNurses[sourceNurseIndex].isPublished = false;
					targetNurses[targetNurseIndex].isPublished = false;

					// Set swapWith to track the swap operation
					sourceNurses[sourceNurseIndex].swapWith = targetNurses[targetNurseIndex].nurseId;
					targetNurses[targetNurseIndex].swapWith = sourceNurses[sourceNurseIndex].nurseId;
				}

				const updatedWeeks = [...prevData.weeks];
				updatedWeeks[weekIndex] = currWeek;
				return { ...prevData, weeks: updatedWeeks };
			});
			resetSwapState();
		}
	};

	useEffect(() => {
		swapNurseFromSpecificDate();
	}, [swapNurseId, swapNurseWithId, swapDayNo, swapDayNoWith, swapShiftNo, swapShiftNoWith]);

	useEffect(() => {
		setDefaultTimezone(chosenDepartment.timezone);
	}, [chosenDepartment.timezone]);

	useEffect(() => {
		if (!isLoggedIn || !userInfo || !chosenDepartment?.department_id || !accessToken) return;

		const loadInitialData = async () => {
			const timing: InitialLoadTiming = {
				departmentId: chosenDepartment.department_id,
				startedAt: performance.now(),
				finished: false,
				errorMessage: null,
			};
			initialLoad.current = timing;
			track(`${INITIAL_DATA_LOAD_EVENT}_start`, { department_id: timing.departmentId });
			try {
				setIsInitialLoading(true);

				// Department data and nurses are independent — fired together. They cross-merge
				// via updateNurses inside setHospitalData (reading the counterpart from prevData),
				// so order doesn't matter, and nothing reads weeks until isInitialLoading is false.
				const departmentId = isHeadNurse(userInfo.role) ? chosenDepartment?.department_id : undefined;
				const promises = isCacheEnabled ? [loadUnitsAndFloors(chosenDepartment.department_id)] : [refreshNursesData(departmentId)];

				if (isHeadNurse(userInfo.role) && !isCacheEnabled) {
					promises.push(refreshDepartmentData(chosenDepartment?.department_id, true));
				}

				if (isHospital) {
					promises.push(getProceduresData(chosenDepartment?.department_id), getfeedbacks(chosenDepartment?.department_id));
				}

				await Promise.all(promises);
			} catch (e) {
				console.error(e);
				timing.errorMessage = String(e);
			}

			if (timing.errorMessage !== null) {
				trackError(`${INITIAL_DATA_LOAD_EVENT}_error`, {
					department_id: timing.departmentId,
					total_ms: elapsedMs(timing.startedAt),
					error_message: timing.errorMessage,
				});
			}
			timing.finished = true;
			// A load for a department the user has already switched away from must not end the newer load's spinner;
			// the newer load flips it itself, which is what re-runs the success effect below.
			if (initialLoad.current === timing) setIsInitialLoading(false);
		};

		loadInitialData();
		// `accessToken` is a guard, not a trigger: on a switch it updates a render before
		// `chosenDepartment`, so triggering on it would load the old department under the new token (403).
	}, [isLoggedIn, userInfo?.id, chosenDepartment?.department_id, isHospital, isCacheEnabled, loadUnitsAndFloors]);

	// Without department_data, the date staff see the schedule until comes from user_info, which the visibility popup
	// reloads after a change. Adopting the shown department again doesn't copy it, so it's copied here.
	const userInfoDisplayUntilDate = userInfo?.departments?.find((dept) => dept.department_id === chosenDepartment.department_id)?.staffScheduleDisplayUntilDate;
	useEffect(() => {
		if (!isCacheEnabled) return;
		setChosenDepartment((prevDept) =>
			prevDept.staffScheduleDisplayUntilDate?.toMillis() === userInfoDisplayUntilDate?.toMillis()
				? prevDept
				: { ...prevDept, staffScheduleDisplayUntilDate: userInfoDisplayUntilDate },
		);
	}, [isCacheEnabled, userInfoDisplayUntilDate, setChosenDepartment]);

	// Runs after the screens under the provider have rendered, so the time covers loading and the first render.
	useEffect(() => {
		const load = initialLoad.current;
		if (isInitialLoading || !load?.finished) return;
		if (load.errorMessage === null) {
			track(`${INITIAL_DATA_LOAD_EVENT}_success`, {
				department_id: load.departmentId,
				total_ms: elapsedMs(load.startedAt),
				since_page_load_ms: Math.round(performance.now()),
				first_load_in_session: !hasFinishedFirstLoad.current,
			});
		}
		initialLoad.current = null;
		hasFinishedFirstLoad.current = true;
	}, [isInitialLoading, track]);

	useEffect(() => {
		if (!isLoggedIn) return;
		setSwapNurseId(-1);
		setSwapNurseWithId(-1);
		setSwapDayNo(-1);
		setSwapDayNoWith(-1);
		setSwapShiftNo(-1);
		setSwapShiftNoWith(-1);
	}, [weekIndex]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: when the component mounts, meaning the app starts, we set the last refresh timestamp - because other services are already loading data once!
	useEffect(() => {
		localStorageService.resetClientGroupedRefreshData(userInfo.id);
	}, []);

	const addMessageToNurse = useCallback(
		(date: DateTime, shiftType: string, nurseId: number, message: string) => {
			refreshCachedDay(date);
			setHospitalData((prevData) => {
				const slot = findScheduleSlot(prevData.weeks, date, shiftType);
				if (!slot) return prevData;
				const { day, newData } = copyDayData(prevData, slot.weekIndex, slot.shiftIndex, slot.dayIndex);

				day.nurses = day.nurses.map((nurse) => (nurse.nurseId !== nurseId ? nurse : { ...nurse, message: message }));

				return newData;
			});
		},
		[refreshCachedDay],
	);

	const getWeekIndexByDateTime = useCallback(
		(date: DateTime) => {
			const staffingDate = getShiftDisplayDate(date, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).startOf("day");
			// Guard against partially-hydrated weeks during refresh: the parent flow can read
			// week ranges before `weeklyShifts[0].days` is populated, throwing on `.date`.
			return hospitalData.weeks.findIndex((week) => {
				const days = week.weeklyShifts[0]?.days;
				if (!days?.length) return false;
				return days[0].date <= staffingDate && days[days.length - 1].date >= staffingDate;
			});
		},
		[hospitalData.weeks, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);

	const getDayIndexByDateTime = useCallback(
		(date: DateTime) => {
			const weekIndex = getWeekIndexByDateTime(date);
			if (weekIndex === -1) {
				return -1;
			}
			const staffingDate = getShiftDisplayDate(date, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).startOf("day");
			return hospitalData.weeks[weekIndex].weeklyShifts[0].days.findIndex((day) => day.date.valueOf() === staffingDate.valueOf());
		},
		[getWeekIndexByDateTime, hospitalData.weeks, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);

	const getWeekIndexByDate = useCallback(
		(date: string | DateTime) => {
			const dateTime = (typeof date === "string" ? DateTime.fromFormat(date, "yyyy-MM-dd") : date).startOf("day");
			// See note on `getWeekIndexByDateTime` — same partially-hydrated week guard.
			return hospitalData.weeks.findIndex((week) => {
				const days = week.weeklyShifts[0]?.days;
				if (!days?.length) return false;
				return days[0].date <= dateTime && days[days.length - 1].date >= dateTime;
			});
		},
		[hospitalData.weeks],
	);

	const getDayIndexByDate = useCallback(
		(date: string | DateTime) => {
			const dateTime = (typeof date === "string" ? DateTime.fromFormat(date, "yyyy-MM-dd") : date).startOf("day");
			const weekIndex = getWeekIndexByDate(dateTime);

			if (weekIndex === -1) return -1;

			return hospitalData.weeks[weekIndex].weeklyShifts[0].days.findIndex((day) => day.date.valueOf() === dateTime.valueOf());
		},
		[hospitalData.weeks, getWeekIndexByDate],
	);

	const acceptNurseChanges = useCallback(
		(date: DateTime, shiftType: string, nurseId: number, is_actual: boolean) => {
			refreshCachedDay(date);
			writeScheduleDay(date, (prevData) => {
				const slot = findScheduleSlot(prevData.weeks, date, shiftType);
				if (!slot) return prevData;
				const { weekIndex: weekI, shiftIndex: shiftNo, dayIndex: dayNo } = slot;
				const currWeek = { ...prevData.weeks[weekI] };
				const targetShift = currWeek?.weeklyShifts[shiftNo]?.days[dayNo];

				if (is_actual) {
					return acceptActualChange(prevData, currWeek, targetShift, nurseId, weekI, shiftNo, dayNo);
				}

				const nurse = targetShift.nurses.find((n: any) => n.nurseId === nurseId);

				// If nurse doesn't exist, add them
				if (!nurse) {
					console.log(`Error tried to acceptNurseChanges for week ${weekI} nurse ${nurseId}, shiftNo ${shiftNo}, dayNo ${dayNo}, is_actual ${is_actual}`);
					return prevData;
				}

				// Handle move scenario
				if (nurse.isPendingToMove && nurse.originalShift !== undefined && nurse.originalDay !== undefined) {
					const otherShift = currWeek?.weeklyShifts[nurse.originalShift]?.days[nurse.originalDay];
					const otherNurse = otherShift?.nurses.find((n: any) => n.nurseId === nurse?.swapWith);
					if (otherNurse === undefined) {
						console.log(`Error tried to find other nurse for week ${weekI} nurse ${nurseId}, shiftNo ${shiftNo}, dayNo ${dayNo}, is_actual ${is_actual}`);
						return prevData;
					}

					let [addedNurse, removedNurse] = [nurse, otherNurse];
					let addedShift = targetShift;
					let removedShift = otherShift;
					if (nurse.isPendingToDelete) {
						[addedNurse, removedNurse] = [otherNurse, nurse];
						addedShift = otherShift;
						removedShift = targetShift;
					}

					addedShift.nurses = addedShift.nurses.filter((n: any) => n.nurseId !== addedNurse.nurseId);
					removedShift.nurses = removedShift.nurses.filter((n: any) => n.nurseId !== removedNurse.nurseId);

					addedShift.nurses.push({
						...addedNurse,
						isPending: false,
						isPendingToMove: false,
						isPublished: true,
					});
					removedShift.nurses.push({
						...removedNurse,
						isPending: false,
						isPendingToMove: false,
						isPublished: true,
					});
				}
				// Handle swap scenario
				else if (nurse.isPendingToSwap && nurse.swapWith !== undefined && nurse.originalShift !== undefined && nurse.originalDay !== undefined) {
					const originalShift = currWeek?.weeklyShifts[nurse.originalShift]?.days[nurse.originalDay];
					const swapNurse = originalShift?.nurses.find((n: any) => n.nurseId === nurse?.swapWith);

					if (swapNurse) {
						// Remove both nurses from their current positions
						targetShift.nurses = targetShift.nurses.filter((n: any) => n.nurseId !== nurseId);
						originalShift.nurses = originalShift.nurses.filter((n: any) => n.nurseId !== nurse?.swapWith);

						// Add them to their new positions
						targetShift.nurses.push({
							...nurse,
							isPending: false,
							isPendingToSwap: false,
							isPublished: true,
						});
						originalShift.nurses.push({
							...swapNurse,
							isPending: false,
							isPendingToSwap: false,
							isPublished: true,
						});
					}
				}
				// Handle removal scenario
				else if (nurse.isPendingToDelete) {
					targetShift.nurses = targetShift.nurses.filter((n: any) => n.nurseId !== nurseId);
				}
				// Handle regular addition or confirmation scenario
				else {
					nurse.isPending = false;
					nurse.isPublished = true;
				}

				nurse.swapWith = undefined;
				nurse.originalShift = undefined;
				nurse.originalDay = undefined;

				const updatedWeeks = [...prevData.weeks];
				updatedWeeks[weekI] = currWeek;
				return { ...prevData, weeks: updatedWeeks };
			});
		},
		[refreshCachedDay, writeScheduleDay],
	);

	const handleNurseDiscard = (
		newData: HospitalData,
		weekIndex: number,
		nurse: NurseInShift,
		nurseShiftNo: number,
		nurseDayNo: number,
		otherNurse: NurseInShift,
		otherShiftNo: number,
		otherDayNo: number,
	) => {
		// Reset original nurse record
		const [nurseToKeep, nurseToRemove, shiftNo, dayNo] = nurse.isPendingToDelete
			? [nurse, otherNurse, otherShiftNo, otherDayNo]
			: [otherNurse, nurse, nurseShiftNo, nurseDayNo];
		nurseToKeep.isPending = false;
		nurseToKeep.isPendingToDelete = false;
		nurseToKeep.isPublished = true;

		// Remove new nurse record
		const nurseIndex = newData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days[dayNo].nurses.findIndex((n: NurseInShift) => n.nurseId === nurseToRemove.nurseId);
		newData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days[dayNo].nurses.splice(nurseIndex, 1);
		nurseToRemove.isPending = false;
		nurseToRemove.isPublished = true;
		nurseToRemove.isPendingToDelete = false;
	};

	const handleNurseDiscardAfterSave = (nurse: NurseInShift, otherNurse: NurseInShift) => {
		// Reset original nurse record
		const [nurseToKeep, nurseToRemove] = nurse.isPendingToDelete ? [nurse, otherNurse] : [otherNurse, nurse];

		// original record
		nurseToKeep.isPending = true;
		nurseToKeep.isPublished = false;
		nurseToKeep.isPendingToDelete = false;
		nurseToKeep.isDiscard = true;

		// new record
		nurseToRemove.isPendingToDelete = true;
		nurseToRemove.isPublished = false;
		nurseToRemove.isPending = false;
		nurseToRemove.isDiscard = true;
	};

	const handleDeclineChangeAutoScheduler = (
		newData: HospitalData,
		weekIndex: number,
		nurse: NurseInShift,
		nurseShiftNo: number,
		nurseDayNo: number,
		otherNurse: NurseInShift,
		otherShiftNo: number,
		otherDayNo: number,
	) => {
		// Clear pending move flags
		nurse.isPendingToMove = false;
		otherNurse.isPendingToMove = false;

		if (nurse.isPending && !nurse.isPublished) {
			// Handle discarding changes before they were saved
			handleNurseDiscard(newData, weekIndex, nurse, nurseShiftNo, nurseDayNo, otherNurse, otherShiftNo, otherDayNo);
		} else if (nurse.isPublished) {
			// Handle discarding changes after they were saved
			handleNurseDiscardAfterSave(nurse, otherNurse);
		}
	};

	const declineNurseChanges = useCallback(
		(date: DateTime, shiftType: string, nurseId: number, isAutoScheduler: boolean) => {
			refreshCachedDay(date);
			writeScheduleDay(date, (prevData) => {
				const slot = findScheduleSlot(prevData.weeks, date, shiftType);
				if (!slot) return prevData;
				const weekI = slot.weekIndex;
				let shiftNo = slot.shiftIndex;
				let dayNo = slot.dayIndex;
				const { day, newData } = copyDayData(prevData, weekI, shiftNo, dayNo);

				// Find the nurse who initiated the swap
				const nurseIndex = findNurseIndexInShift(newData, weekI, shiftNo, dayNo, nurseId);
				day.nurses[nurseIndex] = { ...day.nurses[nurseIndex] };
				const nurse = day.nurses[nurseIndex];

				if (nurse?.isPendingToMove) {
					let otherShift: number | undefined = nurse.originalShift as number;
					let otherDay: number | undefined = nurse.originalDay as number;
					if (otherShift === null || otherDay === null) {
						// get other shift and day properties
						const otherShiftAndDay = newData.weeks[weekI]?.weeklyShifts[shiftNo].days
							.map((day, dayIndex) => {
								if (dayIndex === dayNo) return null;
								const foundNurse = day.nurses.find((n) => n.nurseId === nurseId && n.originalShift === shiftNo && n.originalDay === dayNo);
								if (foundNurse) return [shiftNo, dayIndex];
								return null;
							})
							.filter((d) => d !== null)[0];
						if (otherShiftAndDay !== null) {
							[otherShift, otherDay] = otherShiftAndDay;
						} else {
							console.log(`Error tried to find other shift and day for week ${weekI} nurse ${nurseId}, shiftNo ${shiftNo}, dayNo ${dayNo}`);
							throw new Error(`Could not find other shift and day for nurse ${nurseId} in week ${weekI}, shift ${shiftNo}, day ${dayNo}`);
						}
					}
					const otherNurseIndex = findNurseIndexInShift(newData, weekI, otherShift as number, otherDay as number, nurseId);
					if (otherNurseIndex !== -1) {
						const otherDayShift = newData.weeks[weekI].weeklyShifts[otherShift as number].days[otherDay as number];
						otherDayShift.nurses[otherNurseIndex] = {
							...otherDayShift.nurses[otherNurseIndex],
						};
						const otherNurse = otherDayShift.nurses[otherNurseIndex];

						if (isAutoScheduler) {
							if (nurse.isPending) {
								handleDeclineChangeAutoScheduler(newData, weekI, nurse, shiftNo, dayNo, otherNurse, otherShift, otherDay);
							}
						} else {
							let isSwapped = false;
							if (nurse.isPendingToDelete) {
								isSwapped = true;
							}
							nurse.isPendingToMove = false;
							nurse.originalShift = undefined;
							nurse.originalDay = undefined;
							nurse.isPending = false;
							nurse.isPendingToDelete = false;
							otherNurse.isPendingToMove = false;
							otherNurse.originalShift = undefined;
							otherNurse.originalDay = undefined;
							otherNurse.isPending = false;
							otherNurse.isPendingToDelete = false;

							if (isSwapped) {
								const tempShiftNo = shiftNo;
								const tempDayNo = dayNo;
								shiftNo = otherShift as number;
								dayNo = otherDay as number;
								otherShift = tempShiftNo;
								otherDay = tempDayNo;
							}

							if (nurse.isPendingToDelete) {
								const nurseIndex = newData?.weeks[weekI]?.weeklyShifts[otherShift as number].days[otherDay as number].nurses.findIndex(
									(n) => n.nurseId === nurseId,
								);
								newData?.weeks[weekI]?.weeklyShifts[otherShift as number].days[otherDay as number].nurses.splice(nurseIndex, 1);
							} else {
								const nurseIndex = newData?.weeks[weekI]?.weeklyShifts[shiftNo].days[dayNo].nurses.findIndex((n) => n.nurseId === nurseId);
								newData?.weeks[weekI]?.weeklyShifts[shiftNo].days[dayNo].nurses.splice(nurseIndex, 1);
							}
						}
					}
				} else if (!nurse || !nurse.swapWith) {
					if (nurse !== undefined) {
						if (nurse.isPendingToDelete) {
							// Keep the nurse and remove the pending status
							nurse.isPending = false;
							nurse.isPendingToDelete = false;
						} else {
							// Remove the nurse if pending for addition
							const nurseIndex = newData?.weeks[weekI]?.weeklyShifts[shiftNo].days[dayNo].nurses.findIndex((n) => n.nurseId === nurseId);
							newData?.weeks[weekI]?.weeklyShifts[shiftNo].days[dayNo].nurses.splice(nurseIndex, 1);
						}
					}
				} else if (nurse?.swapWith) {
					const swapNurseIndex = findNurseIndexInShift(newData, weekI, nurse.originalShift as number, nurse.originalDay as number, nurse.swapWith);
					if (swapNurseIndex !== -1) {
						// Return both nurses to their original shift and day
						moveNurse(newData, weekI, shiftNo, dayNo, nurse.originalShift as number, nurse.originalDay as number, nurseId);
						moveNurse(newData, weekI, nurse.originalShift as number, nurse.originalDay as number, shiftNo, dayNo, nurse.swapWith);
					}
					if (nurse.isPendingToDelete) {
						nurse.isPendingToDelete = false;
						nurse.isPending = false;
					}
				}

				return newData;
			});
		},
		[refreshCachedDay, writeScheduleDay],
	);

	const resetNurseProperties = (nurse: any) => {
		return {
			...nurse,
			isPending: false,
			isPendingToSwap: false,
			isPendingToDelete: false,
			swapWith: undefined,
			originalShift: undefined,
			originalDay: undefined,
			isPublished: false,
			hasAcceptedSwap: false,
		};
	};

	const moveNurse = (data: any, weekIdx: number, fromShiftNo: number, fromDayNo: number, toShiftNo: number, toDayNo: number, nurseId: number) => {
		// Check if the referenced objects exist
		if (data.weeks?.[weekIdx]?.weeklyShifts?.[fromShiftNo]?.days?.[fromDayNo] && data.weeks?.[weekIdx]?.weeklyShifts?.[toShiftNo]?.days?.[toDayNo]) {
			const fromNurses = data.weeks[weekIdx].weeklyShifts[fromShiftNo].days[fromDayNo].nurses;
			const toNurses = data.weeks[weekIdx].weeklyShifts[toShiftNo].days[toDayNo].nurses;

			const nurseIdx = fromNurses.findIndex((nurse: any) => nurse.nurseId === nurseId);
			if (nurseIdx !== -1) {
				const nurse = fromNurses.splice(nurseIdx, 1)[0];
				toNurses.push(resetNurseProperties(nurse));
			}
		} else {
			// Handle the undefined case, maybe log an error or throw an exception
			console.error("Attempted to move a nurse, but the shift or day was not found");
		}
	};

	const onEnterSwap = (originalShift: number, originalDay: number, targetShift: number, targetDay: number, nurseId: number, nurseWithId: number) => {
		setSwapDayNo(originalDay);
		setSwapShiftNo(originalShift);
		setSwapNurseId(nurseWithId);
		setSwapDayNoWith(targetDay);
		setSwapShiftNoWith(targetShift);
		setSwapNurseWithId(nurseId);
	};

	const findNurseIndexInShift = (data: HospitalData, weekIndex: number, shiftNo: number, dayNo: number, nurseId: number) => {
		return data.weeks[weekIndex]?.weeklyShifts[shiftNo].days[dayNo].nurses.findIndex((n: any) => n.nurseId === nurseId);
	};

	const resetSwapState = () => {
		setSwapNurseId(-1);
		setSwapNurseWithId(-1);
		setSwapDayNo(-1);
		setSwapDayNoWith(-1);
		setSwapShiftNo(-1);
		setSwapShiftNoWith(-1);
	};

	// With the cache on `hospitalData.nurses` is empty, and the screen saving has loaded the roster query.
	const rosterNurseOf = (data: HospitalData, nurseId: number): Nurse | undefined =>
		isCacheEnabled ? readRosterNurse(chosenDepartment.department_id, nurseId) : data.nurses.find((nurse) => nurse.id === nurseId);

	const prepareDataForAddNurse = (
		hospitalData: HospitalData,
		date: DateTime,
		shiftType: string,
		nurseId: number,
		location?: string,
		addedFromActual = false,
		requestId?: string,
		shiftTimes?: ShiftTimes,
		nurseInShiftId?: string,
		shiftBonus?: number,
		isDraftMode?: boolean,
	): NurseInShift | undefined => {
		const fullNurse = rosterNurseOf(hospitalData, Number(nurseId));
		if (!fullNurse) return undefined;
		return buildNurseShift(
			{ nurse: fullNurse, date, shiftType, location, addedFromActual, requestId, shiftTimes, nurseInShiftId, shiftBonus, isDraftMode: isDraftMode === true },
			chosenDepartment,
			isHospital,
			shouldReverseNameFormat,
		);
	};

	const addNurseToShift = (nurseInShift: NurseInShift, date: DateTime, shiftType: string, addedFromActual = false, isDraftMode = false): void => {
		if (!isDraftMode) refreshCachedDay(date);
		// The profile's X-week grid can add to a week the staffing data was never fetched for. There is no local day to
		// patch there, so the next fetch brings the shift in.
		writeScheduleDay(date, (oldData) => {
			const slot = findScheduleSlot(oldData.weeks, date, shiftType);
			if (!slot) return oldData;
			const { day, newData } = copyDayData(oldData, slot.weekIndex, slot.shiftIndex, slot.dayIndex);

			// Set isPending if in draft mode
			const nurseToAdd = isDraftMode ? { ...nurseInShift, isPending: true } : nurseInShift;

			const targetNurseArray = addedFromActual ? day.nursesActual : day.nurses;
			if (targetNurseArray && !targetNurseArray.some((nurse) => nurse.nurseId === Number(nurseToAdd.nurseId))) {
				if (addedFromActual) {
					day.nursesActual = [...targetNurseArray, nurseToAdd];
				} else {
					day.nurses = [...targetNurseArray, nurseToAdd];
				}

				// in case it's predicted and actual exists then add to actual also
				if (!addedFromActual) {
					const nursesActual = day?.nursesActual;
					if (nursesActual && nursesActual.length > 0 && !nursesActual.some((nurse) => nurse.nurseId === Number(nurseToAdd.nurseId))) {
						day.nursesActual = [...nursesActual, nurseToAdd];
					}
				}
			}

			return newData;
		});
	};

	const patchRosterNurse = usePatchRosterNurse(chosenDepartment.department_id);
	const updateNurse = useCallback(
		(updatedNurse: Nurse) => {
			const filteredNurses: Nurse[] = hospitalData.nurses.filter((nurse: Nurse) => nurse.id !== updatedNurse.id);
			setHospitalData((prevData) => {
				return { ...prevData, nurses: [...filteredNurses, updatedNurse] };
			});
			// Pages that read the roster query instead of `hospitalData.nurses` (the schedule template) see the change too.
			patchRosterNurse(updatedNurse);
		},
		[hospitalData.nurses, patchRosterNurse],
	);

	const removeNurseFromSpecificDate = useCallback(
		(
			date: DateTime,
			shiftType: string,
			nurseIdToRemove: number,
			removeFromActual = false,
			id?: string,
			isDraftMode = false,
			message?: string,
			forceRemove = false,
		) => {
			if (!isDraftMode) refreshCachedDay(date);
			writeScheduleDay(date, (prevData) => {
				const slot = findScheduleSlot(prevData.weeks, date, shiftType);
				if (!slot) return prevData;
				const { day, newData } = copyDayData(prevData, slot.weekIndex, slot.shiftIndex, slot.dayIndex);

				// In draft mode, always mark as pending to delete instead of removing.
				// `forceRemove` bypasses pending-mode (used for agency: scheduled and actual
				// rows mirror each other, so leaving a strikethrough on the other side
				// would still show as "no punch" — see INH-3359).
				const shouldMarkPending = !forceRemove && (isDraftMode || (!isSnf && !isRealDemo));

				if (removeFromActual) {
					if (shouldMarkPending) {
						if (id) {
							day.nursesActual = day.nursesActual?.map((nurse) =>
								nurse.id !== id
									? nurse
									: {
											...nurse,
											isPending: true,
											isPendingToDelete: true,
											isPublished: false,
											message: message,
										},
							);
						} else {
							day.nursesActual = day.nursesActual?.map((nurse) =>
								nurse.nurseId !== nurseIdToRemove
									? nurse
									: {
											...nurse,
											isPending: true,
											isPendingToDelete: true,
											isPublished: false,
											message: message,
										},
							);
						}
					} else {
						// Only actually remove if not in draft mode and is SNF
						if (id) {
							day.nursesActual = day.nursesActual?.filter((nurse) => nurse.id !== id);
						} else {
							day.nursesActual = day.nursesActual?.filter((nurse) => nurse.nurseId !== nurseIdToRemove);
						}
					}
				} else {
					if (shouldMarkPending) {
						if (id) {
							day.nurses = day.nurses.map((nurse) =>
								nurse.id !== id
									? nurse
									: {
											...nurse,
											isPending: true,
											isPublished: false,
											isPendingToDelete: true,
											message: message,
										},
							);
						} else {
							day.nurses = day.nurses.map((nurse) =>
								nurse.nurseId !== nurseIdToRemove
									? nurse
									: {
											...nurse,
											isPending: true,
											isPublished: false,
											isPendingToDelete: true,
											message: message,
										},
							);
						}

						// in case of removing nurse from predicted and actual exists then mark pending delete in actual also
						const nursesActual = day?.nursesActual;
						if (nursesActual && nursesActual.length > 0) {
							day.nursesActual = day.nursesActual?.map((nurse) =>
								nurse.nurseId !== nurseIdToRemove
									? nurse
									: {
											...nurse,
											isPending: true,
											isPendingToDelete: true,
											isPublished: false,
										},
							);
						}
					} else {
						// Only actually remove if not in draft mode and is SNF
						if (id) {
							day.nurses = day.nurses.filter((nurse) => nurse.id !== id);
							day.nursesActual = day.nursesActual?.filter((nurse) => nurse.id !== id);
						} else {
							day.nurses = day.nurses.filter((nurse) => nurse.nurseId !== nurseIdToRemove);
							day.nursesActual = day.nursesActual?.filter((nurse) => nurse.nurseId !== nurseIdToRemove);
						}
					}
				}

				return newData;
			});
		},
		[isSnf, isRealDemo, refreshCachedDay, writeScheduleDay],
	);

	const moveNurseToSpecificDate = (fromDate: DateTime, fromShiftType: string, toDate: DateTime, toShiftType: string, nurseId: number, newNurseId?: string) => {
		// A pending move records the other end by its day in the same week.
		if (!getWeekStart(fromDate, DEFAULT_WEEK_START_DAY).hasSame(getWeekStart(toDate, DEFAULT_WEEK_START_DAY), "day")) return;
		const from = { date: fromDate, shiftType: fromShiftType };
		const to = { date: toDate, shiftType: toShiftType };
		refreshCachedDay(fromDate);
		refreshCachedDay(toDate);
		// Each end is an edit of its own day, so a draft on the schedule cache holds both.
		writeScheduleDay(fromDate, (prevData) => withMoveSource(prevData, from, to, nurseId, chosenDepartment.shift_types, isHospital));
		const fullNurse = rosterNurseOf(hospitalData, nurseId);
		if (!fullNurse) return;
		const defaultTimes = calculateShiftTimesWithTZ({ date: toDate }, chosenDepartment.shift_types.indexOf(toShiftType as ShiftType), fullNurse.role);
		const movedShift: NurseInShift = {
			id: newNurseId ?? uuidv4(),
			nurseId: nurseId,
			departmentId: chosenDepartment.department_id,
			isPending: true,
			isPendingToDelete: false,
			isPendingToSwap: false,
			isPublished: false,
			isPendingToMove: true,
			startTime: defaultTimes.startTime,
			endTime: defaultTimes.endTime,
			role: fullNurse.role,
			name: formatPersonFullName(fullNurse, shouldReverseNameFormat),
			charge: fullNurse.isShiftManager,
			type: fullNurse.employmentType as EmploymentType,
			shiftType: toShiftType,
			notes: "",
			trainingStatus: fullNurse.trainingStatus,
			bonus: 0,
			breaks: [],
		};
		writeScheduleDay(toDate, (prevData) => withMoveTarget(prevData, to, from, movedShift, chosenDepartment.shift_types));
	};

	const restoreNurseToSpecificDate = (date: DateTime, shiftType: string, nurseIdToRestore: number, restoredFromActual = false, isDraftMode?: boolean) => {
		if (!isDraftMode) refreshCachedDay(date);
		writeScheduleDay(date, (prevData) => {
			const slot = findScheduleSlot(prevData.weeks, date, shiftType);
			if (!slot) return prevData;
			const { day, newData } = copyDayData(prevData, slot.weekIndex, slot.shiftIndex, slot.dayIndex);
			const restored = <Shift extends NurseInShift | ActualNurse>(shift: Shift, isPending: boolean): Shift => ({
				...shift,
				isPending,
				isPendingToDelete: false,
			});

			if (restoredFromActual) {
				if (day.nursesActual) day.nursesActual = withFirstOfNurse(day.nursesActual, nurseIdToRestore, (nurse) => restored(nurse, isHospital || !!isDraftMode));
				return newData;
			}
			if (!day.nurses.some((nurse) => nurse.nurseId === nurseIdToRestore)) return newData;
			day.nurses = withFirstOfNurse(day.nurses, nurseIdToRestore, (nurse) => restored(nurse, isHospital || !!isDraftMode));
			// Restored from the schedule while an actual exists: the actual comes back too.
			if (day.nursesActual?.length) day.nursesActual = withFirstOfNurse(day.nursesActual, nurseIdToRestore, (nurse) => restored(nurse, !!isDraftMode));
			return newData;
		});
	};

	const getCensusColor = (shiftNo: number, dayNo: number) => {
		const data = hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days[dayNo];
		if (data && data.censusDifficulty.toLowerCase() === "high") return "#FFA96A";
		if (data && data.censusDifficulty.toLowerCase() === "low") return "#5EB8F9";
		return NEUTRAL[500];
	};

	const getColorByDifficultyName = (difficulty: string | undefined) => {
		const difficultyLower = difficulty?.toLowerCase();
		if (difficultyLower === "high") return isHospitalView ? HOSPITAL_DEMO.NODE_HIGH : "#FFA96A";
		if (difficultyLower === "low") return isHospitalView ? HOSPITAL_DEMO.NODE_LOW : "#5EB8F9";
		return isHospitalView ? HOSPITAL_DEMO.NODE_MEDIUM : NEUTRAL[500];
	};

	const getNurseNameFromId = useCallback(
		(id: number) => {
			// Map lookup, not a roster scan: callers run this inside sort comparators and per-row filters.
			const nurse: Nurse | undefined = nursesMap.get(id);
			return nurse ? formatPersonFullName(nurse, shouldReverseNameFormat) : "";
		},
		[nursesMap, shouldReverseNameFormat],
	);

	// A swap or move suggestion names its other end by position in the same week.
	const suggestionOtherEnd = (date: DateTime, shiftType: string, suggestion: Suggestion): ScheduleDayShift | undefined => {
		const slot = findScheduleSlot(hospitalData.weeks, date, shiftType);
		const otherShift = slot && hospitalData.weeks[slot.weekIndex].weeklyShifts[suggestion.otherShiftNo as number];
		const otherDay = otherShift?.days[suggestion.otherDayNo as number];
		return otherShift && otherDay ? { date: otherDay.date, shiftType: otherShift.type } : undefined;
	};

	const handleSuggestion = (suggestion: Suggestion, date: DateTime, shiftType: string, shiftTimes?: ShiftTimes) => {
		if (suggestion.action === SuggestionAction.Swap) {
			const slot = findScheduleSlot(hospitalData.weeks, date, shiftType);
			if (slot) {
				onEnterSwap(
					slot.shiftIndex,
					slot.dayIndex,
					suggestion.otherShiftNo as number,
					suggestion.otherDayNo as number,
					suggestion.nurseId,
					suggestion.otherNurseId as number,
				);
			}
		} else if (suggestion.action === SuggestionAction.Remove) {
			removeNurseFromSpecificDate(date, shiftType, suggestion.nurseId);
		} else if (suggestion.action === SuggestionAction.Add) {
			const nurseToAdd: NurseInShift | undefined = prepareDataForAddNurse(
				hospitalData,
				date,
				shiftType,
				Number(suggestion.nurseId),
				undefined,
				false,
				suggestion.requestId,
				shiftTimes,
			);

			if (nurseToAdd) {
				addNurseToShift(nurseToAdd, date, shiftType, false);
			}
		} else if (suggestion.action === SuggestionAction.Replace) {
			removeNurseFromSpecificDate(date, shiftType, suggestion.nurseId);
			const nurseToAdd: NurseInShift | undefined = prepareDataForAddNurse(
				hospitalData,
				date,
				shiftType,
				Number(suggestion.otherNurseId),
				undefined,
				false,
				suggestion.requestId,
				shiftTimes,
			);

			if (nurseToAdd) {
				addNurseToShift(nurseToAdd, date, shiftType, false);
			}
		} else if (suggestion.action === SuggestionAction.Move) {
			const otherEnd = suggestionOtherEnd(date, shiftType, suggestion);
			if (otherEnd) moveNurseToSpecificDate(date, shiftType, otherEnd.date, otherEnd.shiftType, suggestion.nurseId);
		}
	};

	const showErrorSnackbar = useErrorSnackbar();

	const updateNurseNotes = (id: string, date: DateTime, shiftType: string, newNotes: string, actual: boolean) => {
		refreshCachedDay(date);
		writeScheduleDay(date, (prevData) => {
			const slot = findScheduleSlot(prevData.weeks, date, shiftType);
			if (!slot) return prevData;
			const { day, newData } = copyDayData(prevData, slot.weekIndex, slot.shiftIndex, slot.dayIndex);
			if (actual) day.nursesActual = day.nursesActual?.map((nurse) => (nurse.id === id ? { ...nurse, notes: newNotes } : nurse));
			else day.nurses = day.nurses.map((nurse) => (nurse.id === id ? { ...nurse, notes: newNotes } : nurse));
			return newData;
		});
	};

	const updateShiftBonus = (id: string, date: DateTime, shiftType: string, newBonus: number, actual?: boolean, isDraftMode = false): void => {
		if (!isDraftMode) refreshCachedDay(date);
		writeScheduleDay(date, (prevData) => {
			const slot = findScheduleSlot(prevData.weeks, date, shiftType);
			if (!slot) return prevData;
			const { day, newData } = copyDayData(prevData, slot.weekIndex, slot.shiftIndex, slot.dayIndex);
			const withBonus = <Shift extends BaseNurseShift>(nurse: Shift): Shift =>
				nurse.id === id ? { ...nurse, bonus: newBonus, ...(isDraftMode ? { isPendingToUpdate: true } : {}) } : nurse;
			if (actual) day.nursesActual = day.nursesActual?.map(withBonus);
			else day.nurses = day.nurses.map(withBonus);
			return newData;
		});
	};

	const updateNurseLocationAndTimes = useCallback(
		({
			nurseInShiftId,
			date,
			shiftType,
			newStartTime,
			newEndTime,
			actual,
			newLocation,
			newJobId,
			newJobName,
			isDraftMode,
		}: {
			nurseInShiftId: string;
			date: DateTime;
			shiftType: string;
			newStartTime?: DateTime;
			newEndTime?: DateTime;
			actual: boolean;
			newLocation?: string;
			newJobId?: string;
			newJobName?: string;
			isDraftMode?: boolean;
		}) => {
			if (!isDraftMode) refreshCachedDay(date);
			writeScheduleDay(date, (prevData) =>
				withShiftDetailsEdited(prevData, {
					shiftId: nurseInShiftId,
					date,
					shiftType,
					newShiftType:
						newStartTime && newEndTime ? getShiftTypeByShiftTimes(chosenDepartment.shift_types, { startTime: newStartTime, endTime: newEndTime }) : shiftType,
					isActual: actual,
					edit: {
						...(newStartTime && newEndTime ? { startTime: newStartTime, endTime: newEndTime } : {}),
						// "" is a cleared location, an edit like any other; only `undefined` leaves the location as it is.
						...(newLocation !== undefined ? { location: newLocation } : {}),
						...(newJobId !== undefined ? { jobId: newJobId, jobName: newJobName } : {}),
						isPendingToUpdate: isDraftMode,
					},
				}),
			);
		},
		[chosenDepartment.shift_types, refreshCachedDay, writeScheduleDay],
	);

	const updateNurseTimes = (
		nurseInShiftId: string,
		date: DateTime,
		shiftType: string,
		newStartTime: DateTime,
		newEndTime: DateTime,
		actual: boolean,
		isDraftMode = false,
	) => {
		updateNurseLocationAndTimes({
			nurseInShiftId,
			date,
			shiftType,
			newStartTime,
			newEndTime,
			actual,
			isDraftMode,
		});
	};

	const updateNurseLocation = (id: string, date: DateTime, shiftType: string, newLocation: string, actual: boolean) => {
		updateNurseLocationAndTimes({
			nurseInShiftId: id,
			date,
			shiftType,
			newLocation,
			actual,
		});
	};

	const updateNurseChargeStatus = useCallback(
		(nurseInShiftId: string, date: DateTime, shiftType: string, isCharge: boolean, isDraftMode = false) => {
			if (!isDraftMode) refreshCachedDay(date);
			writeScheduleDay(date, (prevData) => {
				const slot = findScheduleSlot(prevData.weeks, date, shiftType);
				if (!slot) return prevData;
				const { day, newData } = copyDayData(prevData, slot.weekIndex, slot.shiftIndex, slot.dayIndex);
				day.nurses = day.nurses.map((nurseInShift) =>
					nurseInShift.id === nurseInShiftId
						? {
								...nurseInShift,
								charge: isCharge,
								isPublished: false,
								...(isDraftMode ? { isPendingToUpdate: true } : {}),
							}
						: nurseInShift,
				);

				return newData;
			});
		},
		[refreshCachedDay, writeScheduleDay],
	);

	const updateNurseDayTrainingStatus = useCallback(
		(date: DateTime, nurseId: number, trainingStatus: NurseTrainingStatus, isDraftMode: boolean) => {
			if (!isDraftMode) refreshCachedDay(date);
			writeScheduleDay(date, (prevData) => ({ ...prevData, weeks: withNurseDayTrainingStatus(prevData.weeks, date, nurseId, trainingStatus, isDraftMode) }));
		},
		[refreshCachedDay, writeScheduleDay],
	);

	const getNurseTransformedShifts = (nurseId: number): TransformedShift[] => {
		if (!hospitalData?.weeks?.length || !nurseId) return [];

		const nurse = hospitalData?.nurses.find((n: Nurse) => n.id === nurseId);
		if (!nurse) return [];

		return transformedShiftsOf(hospitalData.weeks, nurse, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes);
	};

	const nurseId = useMemo(() => {
		if (!userInfo.nurses_info) return undefined;
		for (const nurseInfo of userInfo.nurses_info) {
			if (nurseInfo.departments.find((dept) => dept === chosenDepartment.department_id)) {
				return nurseInfo.nurse.id;
			}
		}
	}, [chosenDepartment, userInfo])!;

	const transformedShifts = useMemo(
		() => getNurseTransformedShifts(nurseId),
		[hospitalData, nurseId !== undefined, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);

	const userIdNurseMap = useMemo<{ [userId: string]: Nurse }>(() => {
		return hospitalData.nurses.reduce<{ [userId: string]: Nurse }>((accu, current) => {
			if (current.userId) {
				accu[current.userId] = current;
			}
			return accu;
		}, {});
	}, [hospitalData.nurses]);

	const latestDateInWeeksData = useMemo(() => {
		return hospitalData.weeks
			.at(-1)
			?.weeklyShifts.map((value) => value.days.at(-1)?.date)
			.filter((date) => date !== undefined)
			.sort((a, b) => {
				if (a && b) {
					return a < b ? -1 : 1;
				}
				return 0;
			})
			.at(-1);
	}, [hospitalData.weeks]);

	const departmentEmrType = useMemo<EmrType | undefined>(() => (chosenDepartment ? resolveActiveEmrType(chosenDepartment) : undefined), [chosenDepartment]);

	const roleAverageWage = useMemo(() => roleAverageWagesOf(hospitalData.nurses), [hospitalData.nurses]);

	const getDepartmentDisplayName = useGetDepartmentDisplayName();

	/**
	 * Finds the most specific matching DepartmentLocation for a nurse
	 * Matches based on: location name, roles, and shift times
	 * @param nurse - The nurse to find a location for
	 * @param locations - All department locations
	 * @returns The matching DepartmentLocation or undefined
	 */
	useEffect(() => {
		/*Handle setting default zone for Luxon*/
		Settings.defaultZone = timezone;
	}, [timezone]);

	const getNurseToWeeklyShifts = useCallback(
		(nurseId: Nurse["id"]): Shift[] => {
			const nurse = hospitalData.nurses.find((n) => n.id === nurseId);
			if (!nurse) {
				return [];
			}

			const { weeklyShifts = [] } = hospitalData.weeks.at(weekIndex) || {};
			return weeklyShifts.map<Shift>((shift) => ({
				...shift,
				days: shift.days.map<Day>((day) => ({
					...day,
					nurses: day.nurses.filter((n) => n.nurseId === nurse.id),
					nursesActual: day.nursesActual?.filter((n) => n.nurseId === nurse.id),
				})),
			}));
		},
		[hospitalData, weekIndex],
	);

	if (isInitialLoading) {
		return <FullPageLoadingFallback />;
	}

	const contextValue = {
		hospitalData,
		setHospitalData,
		swapNurseId,
		swapNurseWithId,
		swapDayNo,
		swapDayNoWith,
		swapShiftNo,
		chosenDepartment,
		locationsMap,
		timezone,
		isSnf,
		isAlf,
		isHospital,
		isHospitalDemo,
		isHospitalView,
		isIcuTeleDemo,
		isSnfDemo,
		isAlfDemo,
		isAutoScheduler,
		isInitialLoading,
		isLowry,
		isColonial,
		setChosenDepartment,
		setSwapNurseId,
		setSwapNurseWithId,
		setSwapDayNo,
		setSwapDayNoWith,
		setSwapShiftNo,
		setSwapShiftNoWith,
		getCensusColor,
		addNurseToShift,
		prepareDataForAddNurse,
		acceptNurseChanges,
		addMessageToNurse,
		declineNurseChanges,
		removeNurseFromSpecificDate,
		updateNurse,
		moveNurseToSpecificDate,
		swapNurseFromSpecificDate,
		getColorByDifficultyName,
		getWeekIndexByDateTime,
		getDayIndexByDateTime,
		getWeekIndexByDate,
		getDayIndexByDate,
		restoreNurseToSpecificDate,
		deepCloneObject,
		removeNurseCompletelyFromShift,
		removeNursesShiftsCompletely,
		addNewDBNurseShifts,
		reorderNurses,
		getNurseNameFromId,
		implementedChanges,
		setImplementedChanges,
		handleSuggestion,
		weekI,
		setWeekI,
		getNurseShifts,
		refreshDepartmentData,
		refreshDepartmentWageData,
		refreshNursesData,
		getNurseWeekendShifts,
		getNurseRDOs,
		getNurseRole,
		updateNurseLocation,
		updateNurseNotes,
		updateNurseTimes,
		updateShiftBonus,
		showErrorSnackbar,
		getShiftTimes,
		dateIndexMap,
		calculateShiftTimes: calculateShiftTimesWithTZ,
		updateNurseChargeStatus,
		updateNurseDayTrainingStatus,
		transformedShifts,
		getNurseTransformedShifts,
		getNurseFormattedName,
		getNurseById,
		scheduledShiftsById,
		activeBedsideNurse,
		getShiftDate,
		getScheduleSlot,
		scheduleDayOf,
		getShiftScheduleDates,
		afterScheduleWrite,
		afterPublish,
		refreshScheduleCache,
		writeScheduleDay,
		setDraftDayWriter,
		departmentNurseTypes,
		userIdNurseMap,
		latestDateInWeeksData,
		roleAverageWage,
		departmentEmrType,
		getDepartmentDisplayName,
		getNurseShiftsInTimeFrame,
		calculateDefaultTimesAndLocationForStaff,
		isAnyDemo,
		isRealDemo,
		updateNurseLocationAndTimes,
		getNurseToWeeklyShifts,
		isMedicalDepartment,
		getNurseRoleEnum,
		wageData,
		getWeekRange,
	};
	return <HospitalDataContext.Provider value={contextValue}>{children}</HospitalDataContext.Provider>;
};

export const useHospitalData = () => {
	return useContext(HospitalDataContext);
};

/** A copy of `weeks` whose shift nurses (scheduled and actual) carry their names in the given order. */
function withShiftNamesInOrder(nurses: Nurse[], weeks: WeekData[], lastNameFirst: boolean): WeekData[] {
	const nurseMap = new Map(nurses.map((nurse) => [nurse.id, nurse]));
	const rename = <T extends { nurseId: number; name: string }>(shiftNurse: T): T => {
		const fullNurse = nurseMap.get(shiftNurse.nurseId);
		return fullNurse ? { ...shiftNurse, name: formatPersonFullName(fullNurse, lastNameFirst) } : shiftNurse;
	};
	return weeks.map((week) => ({
		...week,
		weeklyShifts: week.weeklyShifts.map((shift) => ({
			...shift,
			days: shift.days.map((day) => ({
				...day,
				nurses: day.nurses.map(rename),
				...(day.nursesActual && { nursesActual: day.nursesActual.map(rename) }),
			})),
		})),
	}));
}

function updateNurses(nurses: Nurse[], weeks: WeekData[], lastNameFirst: boolean) {
	if (!nurses.length) return;

	const nurseMap = new Map(nurses.map((nurse) => [nurse.id, nurse]));
	const keepKnownNurse = (shift: NurseInShift | ActualNurse): boolean => {
		const fullNurse = nurseMap.get(shift.nurseId);
		if (fullNurse === undefined) return false;
		applyNurseDetails(shift, fullNurse, lastNameFirst);
		return true;
	};

	weeks.forEach((week) => {
		week.weeklyShifts.forEach((shift) => {
			shift.days.forEach((day) => {
				day.nurses = day.nurses.filter(keepKnownNurse);
				if (day.nursesActual) {
					day.nursesActual = day.nursesActual.filter(keepKnownNurse);
				}
			});
		});
	});
}

function acceptActualChange(prevData: HospitalData, currWeek: WeekData, targetShift: Day, nurseId: number, weekI: number, shiftNo: number, dayNo: number) {
	const nurse = targetShift.nursesActual?.find((n: ActualNurse) => n.nurseId === nurseId);
	if (!nurse) {
		console.log(`Error tried to acceptNurseChanges for week ${weekI} nurse ${nurseId}, shiftNo ${shiftNo}, dayNo ${dayNo}, is_actual ${true}`);
		return prevData;
	}
	if (nurse.isPendingToDelete) {
		targetShift.nursesActual = targetShift.nursesActual?.filter((n: ActualNurse) => n.nurseId !== nurseId);
	}
	// Handle regular addition or confirmation scenario
	else {
		nurse.isPending = false;
		nurse.isPublished = true;
	}

	const updatedWeeks = [...prevData.weeks];
	updatedWeeks[weekI] = currWeek;
	return { ...prevData, weeks: updatedWeeks };
}

export const getNonDefaultShiftTime = (nurseInShift: NurseInShift, day: Day, shiftNo: number, chosenDepartment: Department, timezone: string) => {
	if (!nurseInShift.startTime || !nurseInShift.endTime) {
		return null;
	}

	const defaultTimes = calculateShiftTimes({
		day,
		shiftNo,
		nurseRole: nurseInShift.role,
		chosenDepartment,
		location: nurseInShift.location,
	});
	if (nurseInShift.startTime.valueOf() !== defaultTimes.startTime.valueOf() || nurseInShift.endTime.valueOf() !== defaultTimes.endTime.valueOf()) {
		return {
			actualStart: nurseInShift.startTime,
			actualEnd: nurseInShift.endTime,
		};
	}
	return null;
};
